import React, { useEffect, useRef, useState } from "react";
import HeartLoader from "@/components/HeartLoader";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, FileText, Loader2, Sparkles, AlertTriangle, Lightbulb, ListChecks, BarChart3, Download, Mail, X, Plug, Bell, Wand2, Coins, BookOpen, Share2, Plus, Image as ImageIcon, Film, Copy, Globe, HeartPulse, Dumbbell, Salad, Utensils, Wind, Shirt, Clock, Pill, ShieldAlert, Users, User, MapPin, Fuel, Leaf, Car, TreePine, Stethoscope, Store, Cross, CloudSun, Search as SearchIcon, Briefcase, GraduationCap, Link as LinkIcon, Trash2, FileUp, Umbrella, TrainFront, Navigation, CheckCircle2, ShieldCheck, Receipt, Phone, Star, Flame, Trophy, FlaskConical, Video, ArrowRight, Apple, Carrot, Flower2, Newspaper, Orbit, Activity, LayoutGrid } from "lucide-react";
import { searchCities } from "@/lib/cities";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { BTC_ENABLED } from "@/lib/flags";
import Icon from "@/components/Icon";
import CommunityHub from "@/components/CommunityHub";
import UserReports from "@/components/UserReports";
import UserOutputs from "@/components/UserOutputs";
import TrendingStrip from "@/components/TrendingStrip";
import { FileUpload } from "@/components/FileUpload";
import PurityPower from "@/pages/PurityPower";
import LiveVitals from "@/components/LiveVitals";
import KindFeed from "@/components/KindFeed";
import PeopleSearch from "@/components/PeopleSearch";
import { toast } from "sonner";

export default function Personal() {
  const { user, setUser } = useApp();
  const location = useLocation();
  const [tab, setTab] = useState(() => {
    const q = new URLSearchParams(location.search).get("tab") || "social";
    return PERSONAL_TABS.some((t) => t.id === q && !t.external) ? q : "social";
  });
  useEffect(() => {
    const q = new URLSearchParams(location.search).get("tab") || "social";
    setTab(PERSONAL_TABS.some((t) => t.id === q && !t.external) ? q : "social");
  }, [location.search]);
  const activeTab = PERSONAL_TABS.find((t) => t.id === tab && !t.external);
  const [exploreOpen, setExploreOpen] = useState(false);
  const ExploreList = () => (
    <div className="space-y-1" data-testid="personal-tabs">
      <button data-testid="explore-home" onClick={() => setTab("social")}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left mb-1 transition-colors ${tab === "social" ? "bg-brand text-black" : "bg-brand/10 text-brand hover:bg-brand/20"}`}>
        <Users size={15} /> My Kind home (feed)
      </button>
      {PERSONAL_TABS.filter((t) => t.id !== "social").map((tb) => (
        <button key={tb.id} data-testid={`tab-${tb.id}`}
          onClick={() => (tb.external ? window.open(tb.external, "_blank", "noopener") : setTab(tb.id))}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${tb.external ? "text-brand hover:bg-brand/10" : (tab === tb.id ? "bg-brand text-black" : "text-sec hover:bg-elevated hover:text-[var(--rud-text)]")}`}>
          <tb.icon size={15} /> <span className={tb.italic ? "italic" : ""}>{tb.label}</span>
        </button>
      ))}
    </div>
  );
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-[72px] pb-24">
      <div className="flex gap-6 items-start">
        <div className="flex-1 min-w-0">
      {/* Active tab content — directly below the tabs */}
      {/* Mobile Explore button (hidden on lg where it lives atop the right rail) */}
      <div className="lg:hidden flex justify-end mb-3">
        <button data-testid="explore-toggle-mobile" onClick={() => setExploreOpen((o) => !o)}
          className="btn-primary rounded-full h-10 px-5 text-sm flex items-center gap-2 shrink-0"><LayoutGrid size={16} /> Explore</button>
      </div>
      <div className="mt-0" data-testid="active-tab-content">
        {tab === "regimen" && (
          <div className="flex items-start gap-2 rounded-xl bg-elevated border border-rud p-3 text-xs text-sec mb-3" data-testid="personal-disclaimer">
            <AlertTriangle size={16} className="shrink-0 mt-0.5 text-[var(--rud-text)]" />
            <span>My Kind provides general information and recommendations for knowledge and educational purposes only. This is <b className="text-[var(--rud-text)]">not</b> medical or healthcare advice. For any health-related or medical concerns, please consult your physician or a licensed professional.</span>
          </div>
        )}
        {tab === "regimen" && <Regimen user={user} setUser={setUser} />}
        {tab === "social" && <Social user={user} setUser={setUser} />}
        {tab === "weather" && <Weather />}
        {tab === "insights" && <Insights user={user} setUser={setUser} />}
        {tab === "receipts" && <PurityPower user={user} setUser={setUser} />}
        {tab === "carbon" && <Carbon />}
        {tab === "nearby" && <Nearby />}
        {tab === "books" && <Books user={user} setUser={setUser} />}
        {tab === "story" && <LifeStory user={user} />}
        {tab === "professional" && <Professional user={user} setUser={setUser} />}
        {tab === "habits" && <MyHabits scope="personal" />}
        {tab === "community" && <CommunityHub />}
      </div>

      {/* The Community — compact single-line box */}
      <div className="rud-card rounded-2xl px-4 py-3 mt-5 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="the-community">
        <div className="text-sm font-semibold flex items-center gap-1.5 shrink-0"><Share2 size={15} className="text-brand" /> The Community</div>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          {[
            { to: "/meet", label: "MEET", icon: Video },
            { to: "/transport", label: "TRAVEL", icon: Car },
            { to: "/learn", label: "UNIVERSITY", icon: GraduationCap },
          ].map((x) => (
            <a key={x.to} href={x.to} data-testid={`community-${x.label.toLowerCase()}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide border border-rud text-sec hover:text-brand hover:border-brand/50 transition-colors">
              <x.icon size={14} className="text-brand" /> {x.label}
            </a>
          ))}
        </div>
      </div>

      {activeTab && activeTab.desc && (
        <div className="flex items-start gap-2 mt-4 text-sm text-sec" data-testid="tab-description">
          <activeTab.icon size={16} className="text-brand shrink-0 mt-0.5" />
          <span>{activeTab.desc}</span>
        </div>
      )}

      <div className="flex items-center gap-2 rounded-xl bg-brand/10 border border-brand/30 p-3 text-xs mt-4" data-testid="earn-nudge">
        <Coins size={16} className="text-brand shrink-0" />
        <span className="text-[var(--rud-text)]">Low on credits? <b>Earn more</b> — refer friends for 15% bonus credits and cash out your earnings as money{BTC_ENABLED ? ", Bitcoin" : ""} or credits.</span>
        <a href="/economy" data-testid="earn-nudge-cta" className="ml-auto btn-primary text-xs px-3 py-1.5 shrink-0">Earn credits</a>
      </div>

      {user && <TrendingStrip scope="personal" title="Trending in Personal" />}
        </div>

        <aside className="hidden lg:block w-72 shrink-0 sticky top-24 space-y-3">
          <div className="flex justify-end">
            <button data-testid="explore-toggle" onClick={() => setExploreOpen((o) => !o)}
              className="btn-primary rounded-full h-10 px-5 text-sm flex items-center gap-2">
              <LayoutGrid size={16} /> {exploreOpen ? "Close" : "Explore"}
            </button>
          </div>
          {exploreOpen ? (
            <div data-testid="explore-column" className="rud-card rounded-2xl p-3 max-h-[70vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-xs uppercase tracking-widest text-brand">Explore</span>
                <button data-testid="explore-close" onClick={() => setExploreOpen(false)} className="text-sec hover:text-brand"><X size={15} /></button>
              </div>
              <ExploreList />
            </div>
          ) : (
            <div data-testid="personal-rail" className="space-y-3">
              {[
                { key: "communities", label: "Communities", icon: Share2, items: [
                  { t: "My communities", to: "/personal?tab=community" },
                  { t: "Discover groups", to: "/communities" },
                  { t: "Start a community", to: "/communities" } ] },
                { key: "events", label: "Upcoming Events", icon: Video, items: [
                  { t: "Events near me", to: "/events" },
                  { t: "This weekend", to: "/events" },
                  { t: "Host an event", to: "/events" } ] },
                { key: "travel", label: "Travel & Rides", icon: Car, items: [
                  { t: "Rides near me", to: "/transport" },
                  { t: "Upcoming trips", to: "/transport" },
                  { t: "Offer a ride", to: "/transport" } ] },
                { key: "news", label: "Good News", icon: Newspaper, items: [
                  { t: "Top kind stories", to: "/news" },
                  { t: "From India", to: "/news" },
                  { t: "Around the world", to: "/news" } ] },
              ].map((sec) => (
                <div key={sec.key} className="rud-card rounded-2xl p-3" data-testid={`rail-${sec.key}`}>
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-brand mb-1.5 px-1"><sec.icon size={13} /> {sec.label}</div>
                  {sec.items.map((it, i) => (
                    <a key={i} href={it.to} data-testid={`rail-${sec.key}-${i}`}
                      className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm text-sec hover:text-[var(--rud-text)] hover:bg-elevated transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand/60 shrink-0" /> {it.t}
                    </a>
                  ))}
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>

      {exploreOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/60" data-testid="explore-sheet" onClick={() => setExploreOpen(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-72 max-w-[85%] bg-elevated border-l border-rud p-3 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-xs uppercase tracking-widest text-brand">Explore</span>
              <button onClick={() => setExploreOpen(false)} className="text-sec hover:text-brand"><X size={15} /></button>
            </div>
            <ExploreList />
          </div>
        </div>
      )}
    </div>
  );
}

const PERSONAL_TABS = [
  { id: "social", label: "Kind Social", icon: Users, desc: "" },
  { id: "regimen", label: "Health", icon: HeartPulse, desc: "A personalized daily health & wellness regimen. Educational only — not medical advice." },
  { id: "habits", label: "Habits", icon: ListChecks, desc: "Habits from your books & health regimen, with a daily tracker, goals and streak rewards." },
  { id: "practice", label: "Daily Practice", icon: Flower2, external: "/practice", desc: "" },
  { id: "books", label: "Book Interpretation", icon: BookOpen, desc: "Summarize and interpret any book in your language — concise or detailed." },
  { id: "learn", label: "Learn & Earn", icon: GraduationCap, external: "/learn", desc: "VishwaGuru-University — 10,000+ courses from Yoga to AI & Quantum Computing, with exams and certificates." },
  { id: "receipts", label: "Product Safety", italic: true, icon: FlaskConical, desc: "Scan any grocery receipt or product label — My Kind flags harmful ingredients with a scientific reason, tallies calories, macros, sugar & alcohol, grades your purchase and suggests cleaner alternatives." },
  { id: "events", label: "Community Events", icon: Utensils, external: "/events", desc: "" },
  { id: "food", label: "Food", icon: Salad, external: "/meals", desc: "" },
  { id: "travel", label: "Travel", icon: Car, external: "/transport", desc: "" },
  { id: "news", label: "Good News", icon: Newspaper, external: "/news", desc: "" },
  { id: "galactopedia", label: "Galactopedia", icon: Orbit, external: "/galactopedia", desc: "" },
  { id: "weather", label: "Weather", icon: CloudSun, desc: "Live weather for your exact location with what to wear & carry, driving tips, and traffic/transit delay alerts." },
  { id: "community", label: "Community", icon: Share2, desc: "Connect with the My Kind community — share posts and discover others." },
  { id: "insights", label: "Spend Analysis", icon: FileText, desc: "Upload any document (medical reports, bank statements, contracts) and get a clear interpretation." },
  { id: "carbon", label: "Carbon Footprint", icon: Leaf, desc: "Estimate and track your carbon footprint and get ways to reduce it." },
  { id: "story", label: "Life Story", icon: Film, desc: "Weave your life moments — photos & video — into a shareable story." },
  { id: "nearby", label: "Nearby", icon: MapPin, desc: "Discover useful places and services near your precise location." },
  { id: "professional", label: "Professional", icon: Briefcase, desc: "Your public professional CV — experience, education, skills & projects. Autofill from your resume." },
  { id: "weather", label: "Weather", icon: CloudSun, desc: "Live weather for your exact location with what to wear & carry, driving tips, and traffic/transit delay alerts." },
];

function Weather() {
  const { user, setUser } = useApp();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const run = () => {
    setErr("");
    if (!user) { toast.error("Sign in to get your weather brief."); return; }
    if (!navigator.geolocation) { setErr("Location isn't available on this device."); return; }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data: res } = await api.post("/weather", { lat: pos.coords.latitude, lon: pos.coords.longitude });
        setData(res);
        if (setUser && user) setUser({ ...user, credits: res.credits_remaining ?? user.credits });
      } catch (e) { setErr(apiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    }, (e) => { setErr("Please allow location access to get a precise weather brief."); setLoading(false); }, { enableHighAccuracy: true, timeout: 12000 });
  };

  const c = data?.current; const r = data?.recommendations;
  const Chip = ({ icon: I, items, label, testid }) => (items?.length ? (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-sec mb-1.5 flex items-center gap-1.5"><I size={13} className="text-brand" /> {label}</div>
      <div className="flex flex-wrap gap-1.5">{items.map((x, i) => <span key={i} className="text-xs rounded-full bg-brand/10 text-brand px-3 py-1">{x}</span>)}</div>
    </div>
  ) : null);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6" data-testid="weather-panel">
      {!data && (
        <div className="rud-card rounded-2xl p-8 text-center">
          <CloudSun size={40} className="text-brand mx-auto mb-3" />
          <h3 className="font-serif text-2xl">Your live weather brief</h3>
          <p className="text-sec text-sm mt-1 max-w-md mx-auto">Uses your precise location to tell you the weather, what to wear & carry, how to drive, and if rain or heat might slow traffic or trains.</p>
          {err && <p className="text-sm text-red-400 mt-3" data-testid="weather-error">{err}</p>}
          <button data-testid="weather-get" onClick={run} disabled={loading} className="btn-primary h-11 px-6 mt-4 inline-flex items-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Navigation size={16} />} {loading ? "Getting your weather…" : "Use my location"}
          </button>
        </div>
      )}
      {data && (
        <div className="space-y-4">
          <div className="rud-card rounded-2xl p-6" data-testid="weather-current">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-sec flex items-center gap-1.5"><MapPin size={13} className="text-brand" /> {c.location}</div>
                <div className="font-serif text-5xl tracking-tighter mt-2" data-testid="weather-temp">{c.temp_c}°C</div>
                <div className="text-sec text-sm mt-1">{c.desc} · feels {c.feels_c}°C</div>
              </div>
              <CloudSun size={48} className="text-brand shrink-0" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 text-sm">
              <div><div className="text-sec text-xs">High / Low</div>{c.max_c}° / {c.min_c}°</div>
              <div><div className="text-sec text-xs">Rain chance</div>{c.rain_chance_pct}%</div>
              <div><div className="text-sec text-xs">Humidity</div>{c.humidity}%</div>
              <div><div className="text-sec text-xs">Wind</div>{c.wind_kmph} km/h</div>
            </div>
            {r?.summary && <p className="text-sm mt-4 border-t border-rud pt-4">{r.summary}</p>}
          </div>
          <div className="rud-card rounded-2xl p-6 space-y-4" data-testid="weather-recos">
            <Chip icon={Shirt} items={r?.wear} label="What to wear" testid="weather-wear" />
            <Chip icon={Umbrella} items={r?.carry} label="What to carry" testid="weather-carry" />
            <Chip icon={Car} items={r?.driving} label="Driving tips" testid="weather-driving" />
            {r?.transit_note && (
              <div data-testid="weather-transit">
                <div className="text-[11px] uppercase tracking-widest text-sec mb-1.5 flex items-center gap-1.5"><TrainFront size={13} className="text-brand" /> Traffic & transit</div>
                <p className="text-sm text-sec">{r.transit_note}</p>
              </div>
            )}
            <p className="text-[10px] text-sec pt-2 border-t border-rud">{data.note}</p>
          </div>
          <button data-testid="weather-refresh" onClick={run} disabled={loading} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-2">{loading ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />} Refresh</button>
        </div>
      )}
    </motion.div>
  );
}



function Insights({ user, setUser, receipts = false }) {
  const { lang } = useApp();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [goal, setGoal] = useState(receipts ? "Interpret this receipt: itemize the spend, taxes and categories, spot duplicate or unusual charges, and suggest where I can save." : "");
  const [mode, setMode] = useState("summarize");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [drag, setDrag] = useState(false);
  const [email, setEmail] = useState("");

  const run = async () => {
    if (!file) { toast.error("Please choose a file first."); return; }
    setLoading(true); setRes(null);
    const fd = new FormData(); fd.append("file", file); fd.append("goal", goal); fd.append("mode", mode); fd.append("lang", lang);
    try {
      const { data } = await api.post("/personal/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(data.result); setReportId(data.report_id);
      if (data.credits_remaining != null && user) setUser({ ...user, credits: data.credits_remaining });
      toast.success("My Kind prepared your personal report");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const download = async () => {
    try {
      const r = await api.get(`/personal/report/${reportId}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "My Kind-Personal-Report.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download report."); }
  };

  const sendEmail = async () => {
    if (!email) { toast.error("Enter an email."); return; }
    try { await api.post("/personal/email", { report_id: reportId, email }); toast("Report emailed (demo)", { description: email }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const buy = async () => {
    if (!user) { toast.error("Sign in first."); return; }
    try { const { data } = await api.post("/personal/buy"); setUser({ ...user, credits: data.credits }); toast.success("+100 credits added (₹3000 · demo)"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4 mt-6">
      <div className="rud-card rounded-2xl p-6">
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) { setFile(f); setRes(null); } }}
          onClick={() => inputRef.current?.click()}
          data-testid="personal-dropzone"
          className={`cursor-pointer rounded-xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-brand bg-white/5" : "border-rud"}`}
        >
          <input ref={inputRef} type="file" className="hidden" data-testid="personal-file-input"
            accept=".pdf,.txt,.md,.csv,.json,.docx,.png,.jpg,.jpeg,.webp"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setRes(null); } }} />
          {file ? (
            <div className="flex items-center justify-center gap-3"><FileText className="text-brand" /><span className="text-sm">{file.name}</span>
              <button onClick={(e) => { e.stopPropagation(); setFile(null); setRes(null); }} className="text-sec hover:text-red-400"><X size={16} /></button></div>
          ) : (
            <><Upload className="mx-auto text-brand mb-3" /><p className="text-sm">Drop a receipt, report or statement</p><p className="text-xs text-sec mt-1">PDF · Image · TXT · CSV · DOCX</p></>
          )}
        </div>

        <label className="text-xs uppercase tracking-widest text-sec mt-5 block">Depth</label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <button data-testid="mode-summarize" onClick={() => setMode("summarize")} className={`rounded-lg p-3 text-left border ${mode === "summarize" ? "border-brand bg-white/5" : "border-rud"}`}>
            <div className="text-sm font-medium">Summarize</div><div className="text-[11px] text-sec">Summary & gist</div>
          </button>
          <button data-testid="mode-elaborate" onClick={() => setMode("elaborate")} className={`rounded-lg p-3 text-left border ${mode === "elaborate" ? "border-brand bg-white/5" : "border-rud"}`}>
            <div className="text-sm font-medium">Elaborate</div><div className="text-[11px] text-sec">Detailed interpretation</div>
          </button>
        </div>

        <label className="text-xs uppercase tracking-widest text-sec mt-5 block">Your goal</label>
        <textarea data-testid="personal-goal" value={goal} onChange={(e) => setGoal(e.target.value)} rows={3}
          placeholder="e.g. Cut my monthly spend, understand my blood report, plan a budget…"
          className="rud-input w-full rounded-lg px-3 py-2 mt-2 text-sm resize-none" />
        <button data-testid="personal-run" onClick={run} disabled={loading} className="btn-primary w-full h-11 mt-4 flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <><Loader2 size={18} className="animate-spin" /> Analyzing…</> : <><Wand2 size={16} /> Get my report</>}
        </button>

        <div className="mt-4 rounded-lg bg-elevated border border-rud p-3 text-xs text-sec">
          <div className="flex items-center gap-2 text-[var(--rud-text)] mb-1"><Coins size={14} className="text-brand" /> Personal plan — Bharat (India)</div>
          Start with <b className="text-[var(--rud-text)]">₹3,000 → 100 credits</b>, then ₹54/credit. Each report costs 10 credits.
          <button data-testid="personal-buy" onClick={buy} className="btn-primary text-xs px-3 py-1.5 mt-2">Get 100 credits · ₹3,000</button>
        </div>
      </div>

      <div className="rud-card rounded-2xl p-6 min-h-[320px]">
        <AnimatePresence mode="wait">
          {!res && !loading && (
            <motion.div key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full grid place-items-center text-center text-sec">
              <div><Sparkles size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">Your personal insights will appear here.</p></div>
            </motion.div>
          )}
          {loading && (
            <motion.div key="l" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full grid place-items-center text-sec">
              <div className="text-center"><HeartLoader /><p className="text-sm">Reading & analyzing…</p></div>
            </motion.div>
          )}
          {res && (
            <motion.div key="r" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="personal-result">
              {res.document_type && <span className="text-xs px-2.5 py-1 rounded-full bg-brand text-black">{res.document_type}</span>}
              <p className="text-sm leading-relaxed">{res.summary}</p>
              {res.metrics?.length > 0 && (
                <div><div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><BarChart3 size={14} /> Key figures</div>
                  <div className="grid grid-cols-2 gap-2">{res.metrics.map((m, i) => (
                    <div key={i} className="rounded-lg bg-elevated border border-rud p-2.5"><div className="text-xs text-sec">{m.label}</div><div className="text-sm font-medium">{m.value}</div></div>
                  ))}</div></div>
              )}
              <Block icon={Lightbulb} title="Insights" items={res.insights} />
              <Block icon={ListChecks} title="Recommendations" items={res.recommendations} />
              {res.disclaimer && <div className="flex gap-2 rounded-lg bg-elevated border border-rud p-3 text-xs text-sec"><AlertTriangle size={16} className="shrink-0 text-[var(--rud-text)]" /> {res.disclaimer}</div>}
              <div className="flex flex-col gap-2 pt-1">
                <button data-testid="personal-download" onClick={download} className="btn-primary h-10 flex items-center justify-center gap-2"><Download size={16} /> Download PDF</button>
                <div className="flex gap-2">
                  <input data-testid="personal-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="rud-input flex-1 rounded-lg px-3 text-sm" />
                  <button data-testid="personal-email-send" onClick={sendEmail} className="btn-ghost rounded-lg px-4 text-sm flex items-center gap-1"><Mail size={15} /> Email</button>
                </div>
                <p className="text-[11px] text-sec">Email delivery is a demo until an email provider is configured.</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Books({ user, setUser }) {
  const { lang } = useApp();
  const [title, setTitle] = useState("");
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("concise");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [resMode, setResMode] = useState("concise");
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const [vecUrl, setVecUrl] = useState("");
  const [books, setBooks] = useState([]);
  const [openBook, setOpenBook] = useState(null);
  const loadBooks = async () => { if (!user) return; try { const { data } = await api.get("/personal/books"); setBooks(data.books || []); } catch { /* */ } };
  useEffect(() => { loadBooks(); /* eslint-disable-next-line */ }, [user]);

  // Generate a topic-specific monochrome + heart-red share vector for the interpreted book (cached by title).
  useEffect(() => {
    if (!res?.title) { setVecUrl(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/search/share", {
          query: `${res.title}${res.author ? ` by ${res.author}` : ""}`,
          kind: "book",
          summary: (res.summary || "").slice(0, 240),
          href: "/personal?tab=books",
        });
        if (!cancelled && data?.url) setVecUrl(data.url);
      } catch { /* keep origin fallback */ }
    })();
    return () => { cancelled = true; };
  }, [res?.title, res?.author]);

  const run = async () => {
    if (!title.trim()) { toast.error("Type a book title."); return; }
    if (!user) { toast.error("Sign in to interpret books."); return; }
    setLoading(true); setRes(null); setShared(false);
    try {
      const { data } = await api.post("/personal/book", { title, query, mode, lang });
      setRes(data.result); setResMode(data.mode || mode);
      if (data.credits_remaining != null && user) setUser({ ...user, credits: data.credits_remaining, free_used: { ...(user.free_used || {}), book: true } });
      if (data.was_free) toast.success("Your 1 free book summary ✨ — detailed interpretations are paid.");
      else toast.success(`${data.mode === "detailed" ? "Detailed" : "Concise"} interpretation ready${data.cost ? ` · −${data.cost} credits` : ""}`);
      loadBooks();
    } catch (e) { if (e.response?.status !== 402) toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const shareText = () => {
    if (!res) return "";
    const tk = (res.key_takeaways || []).slice(0, 3).map((t) => `• ${t}`).join("\n");
    const link = vecUrl || window.location.origin;
    return `📖 ${res.title}${res.author ? ` — ${res.author}` : ""}\n\n${(res.summary || "").slice(0, 320)}${res.summary?.length > 320 ? "…" : ""}\n\n${tk}\n\nInterpreted for me by My Kind — ${link}`;
  };

  const socialShare = (network) => {
    const text = shareText();
    const url = vecUrl || window.location.origin;
    const enc = encodeURIComponent(text);
    const links = {
      whatsapp: `https://wa.me/?text=${enc}`,
      x: `https://twitter.com/intent/tweet?text=${enc}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${enc}`,
    };
    if (network === "copy") { navigator.clipboard.writeText(text).then(() => toast("Copied to clipboard")).catch(() => {}); return; }
    window.open(links[network], "_blank", "noopener,noreferrer");
  };

  const shareToProfile = async () => {
    if (!user) { toast.error("Sign in to share to your My Kind profile."); return; }
    if (!res) return;
    setSharing(true);
    try {
      const bodyText = [
        res.summary,
        (res.key_takeaways || []).length ? "\nKey takeaways:\n" + res.key_takeaways.map((t) => `• ${t}`).join("\n") : "",
        res.why_for_you ? `\nWhy it's for me: ${res.why_for_you}` : "",
      ].filter(Boolean).join("\n");
      await api.post("/showcase", {
        title: `📖 My take on "${res.title}"${res.author ? ` by ${res.author}` : ""}`,
        body: bodyText.slice(0, 4000),
        category: "talent",
      });
      setShared(true);
      toast.success("Shared to your My Kind profile");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSharing(false); }
  };

  const download = async (format) => {
    if (!res) return;
    try {
      const content = {
        summary: res.summary,
        key_points: res.key_takeaways || [],
        steps: res.action_steps || [],
      };
      if ((res.themes || []).length) content.perspectives = res.themes.map((t) => `${t.heading}: ${t.detail}`);
      if (res.why_for_you) content.recommendation = res.why_for_you;
      const r = await api.post("/export", {
        title: `${res.title}${res.author ? ` — ${res.author}` : ""}`,
        deliverable_label: "Book Interpretation",
        content, format,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `My Kind-Book-${res.title.replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download."); }
  };

  return (
    <div className="mt-6 space-y-4" data-testid="books-tab">
      <div className="grid lg:grid-cols-2 gap-4">
      <div className="rud-card rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-3"><BookOpen className="text-brand" /><h3 className="font-serif text-2xl">Book Interpretation, in your language</h3></div>
        <p className="text-sec text-sm mb-4">Type any book. My Kind interprets it in {`your language`}, personalized from your profile, posts and what you ask — then share it or download.</p>

        <label className="text-xs uppercase tracking-widest text-sec block mb-1.5">Depth</label>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button data-testid="book-mode-concise" onClick={() => setMode("concise")} className={`rounded-lg p-3 text-left border transition-colors ${mode === "concise" ? "border-brand bg-brand/5" : "border-rud"}`}>
            <div className="text-sm font-medium">Concise summary</div><div className="text-[11px] text-sec">{user && !user.free_used?.book ? "Quick gist · 1st free ✨" : "Quick gist · 5 credits"}</div>
          </button>
          <button data-testid="book-mode-detailed" onClick={() => setMode("detailed")} className={`rounded-lg p-3 text-left border transition-colors ${mode === "detailed" ? "border-brand bg-brand/5" : "border-rud"}`}>
            <div className="text-sm font-medium">Detailed & comprehensive</div><div className="text-[11px] text-sec">Themes + action steps · 8 credits</div>
          </button>
        </div>

        <label className="text-xs uppercase tracking-widest text-sec block mb-1.5">Book title</label>
        <input data-testid="book-title" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="e.g. Bhagavad Gita, Atomic Habits, Sapiens…" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />

        <label className="text-xs uppercase tracking-widest text-sec block mt-3 mb-1.5">What do you want from it? (optional)</label>
        <textarea data-testid="book-query" value={query} onChange={(e) => setQuery(e.target.value)} rows={2}
          placeholder="e.g. How can I apply this to build better habits at work?" className="rud-input w-full rounded-lg px-3 py-2 text-sm resize-none" />

        <button data-testid="book-run" onClick={run} disabled={loading} className="btn-primary w-full h-11 mt-4 flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <><Loader2 size={16} className="animate-spin" /> Interpreting…</> : <><Wand2 size={16} /> Interpret this book</>}
        </button>
        <p className="text-[11px] text-sec mt-2">Personalized from your profile & posts — {user ? "signed in" : "sign in for full personalization"}.</p>
      </div>

      <div className="rud-card rounded-2xl p-6 min-h-[280px]">
        {!res && !loading && <div className="h-full grid place-items-center text-center text-sec"><div><BookOpen size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">Your personalized interpretation appears here.</p></div></div>}
        {loading && <div className="h-full grid place-items-center text-sec"><div className="text-center"><HeartLoader /><p className="text-sm">Reading & interpreting for you…</p></div></div>}
        {res && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="book-result">
            <div className="flex items-start justify-between gap-3">
              <div><div className="font-serif text-2xl leading-tight">{res.title}</div>{res.author && <div className="text-sec text-sm">{res.author}</div>}</div>
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-brand/15 text-brand shrink-0">{resMode === "detailed" ? "Detailed" : "Concise"}</span>
            </div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{res.summary}</p>
            <Block icon={ListChecks} title="Key takeaways" items={res.key_takeaways} />
            {(res.themes || []).length > 0 && (
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><Lightbulb size={14} /> Core themes</div>
                <div className="space-y-2">{res.themes.map((th, i) => (
                  <div key={i} className="rounded-lg bg-elevated border border-rud p-3"><div className="text-sm font-medium">{th.heading}</div><div className="text-sm text-sec mt-0.5">{th.detail}</div></div>
                ))}</div>
              </div>
            )}
            <Block icon={ListChecks} title="Action steps for you" items={res.action_steps} />
            {res.why_for_you && <div className="rounded-lg bg-elevated border border-rud p-3 text-sm"><span className="text-brand text-xs uppercase tracking-widest">Why this is for you</span><p className="mt-1 text-sec">{res.why_for_you}</p></div>}
            {res.personalized_note && <div className="rounded-lg bg-brand/10 border border-brand/30 p-3 text-sm text-[var(--rud-text)]">{res.personalized_note}</div>}

            <div className="pt-2 border-t border-rud space-y-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-sec mb-2 flex items-center gap-1.5"><Share2 size={13} /> Share</div>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="book-share-profile" onClick={shareToProfile} disabled={sharing} className="btn-primary text-xs h-9 px-3 flex items-center gap-1.5 disabled:opacity-60">
                    {sharing ? <Loader2 size={13} className="animate-spin" /> : <Users size={13} />} {shared ? "Shared ✓" : "Share to my profile"}
                  </button>
                  <button data-testid="book-share-whatsapp" onClick={() => socialShare("whatsapp")} className="btn-ghost rounded-full text-xs h-9 px-3">WhatsApp</button>
                  <button data-testid="book-share-x" onClick={() => socialShare("x")} className="btn-ghost rounded-full text-xs h-9 px-3">X</button>
                  <button data-testid="book-share-linkedin" onClick={() => socialShare("linkedin")} className="btn-ghost rounded-full text-xs h-9 px-3">LinkedIn</button>
                  <button data-testid="book-share-telegram" onClick={() => socialShare("telegram")} className="btn-ghost rounded-full text-xs h-9 px-3">Telegram</button>
                  <button data-testid="book-share-copy" onClick={() => socialShare("copy")} className="btn-ghost rounded-full text-xs h-9 px-3 flex items-center gap-1"><Copy size={12} /> Copy</button>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-sec mb-2 flex items-center gap-1.5"><Download size={13} /> Download</div>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="book-download-pdf" onClick={() => download("pdf")} className="btn-ghost rounded-full text-xs h-9 px-3">PDF</button>
                  <button data-testid="book-download-docx" onClick={() => download("docx")} className="btn-ghost rounded-full text-xs h-9 px-3">Word</button>
                  <button data-testid="book-download-md" onClick={() => download("md")} className="btn-ghost rounded-full text-xs h-9 px-3">Markdown</button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
      </div>

      {/* Your interpreted books — expandable history */}
      {books.length > 0 && (
        <div className="rud-card rounded-2xl p-6" data-testid="books-history">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><BookOpen size={14} /> Your Interpreted Books</div>
          <p className="text-sec text-sm mt-1">Tap any book to expand its full interpretation again — no re-charge.</p>
          <div className="mt-4 space-y-2">
            {books.map((b) => {
              const open = openBook === b.id;
              const r = b.result || {};
              return (
                <div key={b.id} className="rounded-xl border border-rud bg-white/5 overflow-hidden" data-testid={`book-hist-${b.id}`}>
                  <button onClick={() => setOpenBook(open ? null : b.id)} data-testid={`book-hist-toggle-${b.id}`}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors">
                    <BookOpen size={16} className="text-brand shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{b.title}{r.author ? <span className="text-sec font-normal"> · {r.author}</span> : ""}</div>
                      <div className="text-[11px] text-sec">{b.mode === "detailed" ? "Detailed" : "Concise"} · {new Date(b.created_at).toLocaleDateString()}</div>
                    </div>
                    <ArrowRight size={15} className={`text-sec shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-4 pb-4 space-y-3 border-t border-rud/60 pt-3" data-testid={`book-hist-body-${b.id}`}>
                      {r.summary && <p className="text-sm leading-relaxed whitespace-pre-wrap">{r.summary}</p>}
                      {(r.key_takeaways || []).length > 0 && <Block icon={ListChecks} title="Key takeaways" items={r.key_takeaways} />}
                      {(r.themes || []).length > 0 && (
                        <div className="space-y-2">{r.themes.map((th, i) => (
                          <div key={i} className="rounded-lg bg-elevated border border-rud p-3"><div className="text-sm font-medium">{th.heading}</div><div className="text-sm text-sec mt-0.5">{th.detail}</div></div>
                        ))}</div>
                      )}
                      {(r.action_steps || []).length > 0 && <Block icon={ListChecks} title="Action steps for you" items={r.action_steps} />}
                      {r.why_for_you && <div className="rounded-lg bg-elevated border border-rud p-3 text-sm"><span className="text-brand text-xs uppercase tracking-widest">Why this is for you</span><p className="mt-1 text-sec">{r.why_for_you}</p></div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function LifeStory({ user }) {
  const [story, setStory] = useState(null);
  const [busy, setBusy] = useState("");
  const [m, setM] = useState({ title: "", note: "", media_url: "", media_type: "image", event_type: "Milestone", date: "" });

  const load = async () => { try { const { data } = await api.get("/story"); setStory(data.story); } catch {} };
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return (
    <div className="rud-card rounded-2xl p-10 mt-6 text-center" data-testid="story-signin">
      <Share2 className="mx-auto text-brand mb-3" /><p className="text-sm text-sec">Sign in to build and share your life story.</p>
      <a href="/auth" className="btn-primary h-10 px-6 inline-flex items-center mt-4 text-sm">Sign in</a>
    </div>
  );

  const call = async (key, fn) => { setBusy(key); try { const { data } = await fn(); setStory(data.story || (await api.get("/story")).data.story); return data; } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); } };
  const addMoment = async () => { if (!m.title.trim()) { toast.error("Add a title."); return; } await call("add", () => api.post("/story/moment", m)); setM({ title: "", note: "", media_url: "", media_type: "image", event_type: "Milestone", date: "" }); };
  const delMoment = async (mm, i) => { await call("del", () => api.post("/story/moment/delete", { mid: mm.mid || "", index: i })); };
  const importSM = () => call("import", () => api.post("/story/import"));
  const generate = () => call("gen", () => api.post("/story/generate"));
  const publish = async () => { const d = await call("pub", () => api.post("/story/publish")); if (d?.share_slug) { await load(); toast.success("Published! Link copied"); const link = `${window.location.origin}/story/${d.share_slug}`; try { await navigator.clipboard.writeText(link); } catch {} } };

  const shareLink = story?.share_slug ? `${window.location.origin}/story/${story.share_slug}` : "";

  return (
    <div className="grid lg:grid-cols-2 gap-4 mt-6" data-testid="lifestory">
      <div className="rud-card rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1"><Share2 className="text-brand" /><h3 className="font-serif text-2xl">Your Life Story</h3>
          <span className="ml-auto text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-yellow-500/15 text-yellow-400">Import: Demo</span></div>
        <p className="text-sec text-sm mb-3">Import moments from your connected accounts (LinkedIn, FB, X, Instagram) or add your own photos & videos, then let My Kind weave your story to share.</p>
        <button data-testid="story-import" onClick={importSM} disabled={busy === "import"} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-2 disabled:opacity-60">
          {busy === "import" ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Import from my accounts
        </button>

        <div className="mt-4 space-y-2">
          <input data-testid="moment-title" value={m.title} onChange={(e) => setM({ ...m, title: e.target.value })} placeholder="Moment title" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <select data-testid="moment-type" value={m.event_type} onChange={(e) => setM({ ...m, event_type: e.target.value })} className="rud-input rounded-lg px-2 py-2 text-sm flex-1">
              {["Milestone", "Major event", "Travel", "Celebration", "Occasion", "Accident & recovery", "Achievement"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input data-testid="moment-date" type="date" value={m.date} onChange={(e) => setM({ ...m, date: e.target.value })} className="rud-input rounded-lg px-2 py-2 text-sm w-40" />
          </div>
          <input data-testid="moment-note" value={m.note} onChange={(e) => setM({ ...m, note: e.target.value })} placeholder="A short note" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <input data-testid="moment-media" value={m.media_url} onChange={(e) => setM({ ...m, media_url: e.target.value })} placeholder="Photo or video URL" className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
            <select value={m.media_type} onChange={(e) => setM({ ...m, media_type: e.target.value })} className="rud-input rounded-lg px-2 text-sm">
              <option value="image">Photo</option><option value="video">Video</option>
            </select>
          </div>
          <FileUpload
            category="life_story"
            accept="image/*,video/*"
            label="Upload photo / video"
            className="w-full justify-center"
            testId="moment-upload"
            onUploaded={(f) => setM((prev) => ({ ...prev, media_url: f.absoluteUrl, media_type: (f.content_type || "").startsWith("video") ? "video" : "image" }))}
          />
          <button data-testid="moment-add" onClick={addMoment} disabled={busy === "add"} className="btn-ghost rounded-lg h-9 px-4 text-sm w-full flex items-center justify-center gap-2">
            <Plus size={15} /> Add moment
          </button>
        </div>

        <div className="flex gap-2 mt-4">
          <button data-testid="story-generate" onClick={generate} disabled={busy === "gen"} className="btn-primary h-10 px-4 text-sm flex-1 flex items-center justify-center gap-2 disabled:opacity-60">
            {busy === "gen" ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Weave my story
          </button>
          <button data-testid="story-publish" onClick={publish} disabled={busy === "pub"} className="btn-ghost rounded-lg h-10 px-4 text-sm flex items-center gap-2">
            <Globe size={15} /> Publish & share
          </button>
        </div>
        {shareLink && (
          <div className="mt-3 flex items-center gap-2 bg-elevated rounded-md p-2 font-mono text-xs break-all border border-rud">
            {shareLink}
            <button onClick={async () => { try { await navigator.clipboard.writeText(shareLink); toast("Link copied"); } catch {} }} className="text-brand shrink-0"><Copy size={14} /></button>
          </div>
        )}
      </div>

      <div className="rud-card rounded-2xl p-6 min-h-[280px]">
        {story?.narrative && <p className="text-sm leading-relaxed whitespace-pre-wrap mb-4" data-testid="story-narrative">{story.narrative}</p>}
        <div className="grid grid-cols-2 gap-2">
          {(story?.moments || []).map((mm, i) => (
            <div key={i} className="rounded-lg overflow-hidden border border-rud bg-elevated relative group" data-testid={`moment-${i}`}>
              <button data-testid={`moment-del-${i}`} onClick={() => delMoment(mm, i)} title="Delete" className="absolute top-1 right-1 z-10 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
              {mm.media_url ? (mm.media_type === "video"
                ? <video src={mm.media_url} className="w-full h-24 object-cover" />
                : <img src={mm.media_url} alt="" className="w-full h-24 object-cover" />)
                : <div className="w-full h-24 grid place-items-center text-sec"><ImageIcon size={20} /></div>}
              <div className="p-2">
                {mm.event_type && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand/15 text-brand">{mm.event_type}</span>}
                <div className="text-xs font-medium truncate mt-1">{mm.title}</div>
                <div className="text-[10px] text-sec truncate">{mm.date ? `${mm.date} · ` : ""}{mm.note}</div>
              </div>
            </div>
          ))}
        </div>
        {(!story || story.moments?.length === 0) && <div className="h-40 grid place-items-center text-center text-sec text-sm">Import or add moments to begin.</div>}
      </div>
    </div>
  );
}

function Connections({ user }) {
  const [providers, setProviders] = useState([]);
  const [connected, setConnected] = useState({});
  const [feed, setFeed] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [insights, setInsights] = useState([]);
  const [loadingIns, setLoadingIns] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/connections"); setProviders(data.providers); setConnected(data.connected || {}); }
    catch { /* not signed in */ }
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) {
    return (
      <div className="rud-card rounded-2xl p-10 mt-6 text-center" data-testid="connections-signin">
        <Plug className="mx-auto text-brand mb-3" />
        <p className="text-sm text-sec">Sign in to connect your accounts and contacts. Others can only see your public profile — name, city, gender. We never share your spending, health, address, date of birth, ID or anything private. Just your posts — text, videos, links, images.</p>
        <a href="/auth" className="btn-primary h-10 px-6 inline-flex items-center mt-4 text-sm">Sign in</a>
      </div>
    );
  }

  const toggle = async (id) => {
    try { const { data } = await api.post(`/connections/${id}/toggle`); setConnected(data.connected); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const refreshFeed = async () => {
    setLoadingFeed(true);
    try { const { data } = await api.get("/connections/feed"); setFeed(data.items || []); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoadingFeed(false); }
  };
  const analyzeData = async () => {
    setLoadingIns(true);
    try {
      const { data } = await api.get("/connections/insights");
      setInsights(data.insights || []);
      if (!data.active?.length) toast.error("Connect at least one account first.");
      else toast.success("My Kind analyzed your data — you own it.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoadingIns(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4 mt-6">
      <div className="rud-card rounded-2xl p-6" data-testid="connections-panel">
        <div className="flex items-center gap-2 mb-1"><Plug className="text-brand" /><h3 className="font-serif text-2xl">Your Connections</h3>
          <span className="ml-auto text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-yellow-500/15 text-yellow-400">Demo</span></div>
        <p className="text-sec text-sm mb-4">Connect your accounts and My Kind surfaces smart notifications & recommendations. Live sign-in gets wired once provider keys are added.</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {providers.map((p) => (
            <button key={p.id} data-testid={`connect-${p.id}`} onClick={() => toggle(p.id)}
              className={`flex items-center gap-3 rounded-lg p-3 border text-left transition-colors ${connected[p.id] ? "border-brand bg-white/5" : "border-rud"}`}>
              <Icon name={p.icon} size={18} className="text-brand" />
              <div className="flex-1"><div className="text-sm">{p.name}</div><div className="text-[11px] text-sec">{connected[p.id] ? "Connected" : "Tap to connect"}</div></div>
              <span className={`w-8 h-4 rounded-full relative transition-colors ${connected[p.id] ? "bg-brand" : "bg-elevated"}`}>
                <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${connected[p.id] ? "left-4" : "left-0.5"}`} />
              </span>
            </button>
          ))}
        </div>
        <button data-testid="refresh-feed" onClick={refreshFeed} disabled={loadingFeed} className="btn-primary w-full h-10 mt-4 flex items-center justify-center gap-2 disabled:opacity-60">
          {loadingFeed ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />} Generate notifications & recommendations
        </button>
        <button data-testid="analyze-data" onClick={analyzeData} disabled={loadingIns} className="btn-ghost rounded-full w-full h-10 mt-2 flex items-center justify-center gap-2 disabled:opacity-60">
          {loadingIns ? <Loader2 size={16} className="animate-spin" /> : <BarChart3 size={16} />} Analyze my data — you own it
        </button>
      </div>

      {insights.length > 0 && (
        <div className="lg:col-span-2 rud-card rounded-2xl p-6" data-testid="owned-insights">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <BarChart3 className="text-brand" /><h3 className="font-serif text-2xl">Your life patterns</h3>
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-brand/15 text-brand">You own this data</span>
          </div>
          <p className="text-sec text-sm mb-4">My Kind turned your connected data into insights — activity, eating, buying and health-risk patterns. You own it, and you can monetize it <b>anonymously</b> in My Vault.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {insights.map((ins, i) => (
              <div key={i} className="rounded-lg bg-elevated border border-rud p-4">
                <div className="text-xs uppercase tracking-widest text-brand mb-1">{ins.category}</div>
                <p className="text-sm">{ins.summary}</p>
                <ul className="mt-2 space-y-1">{(ins.patterns || []).map((p, j) => <li key={j} className="text-xs text-sec flex gap-2"><span className="text-brand">·</span>{p}</li>)}</ul>
                {ins.recommendation && <p className="text-xs text-sec italic mt-2 border-l-2 border-brand pl-2">{ins.recommendation}</p>}
              </div>
            ))}
          </div>
          <a href="/vault" data-testid="sell-in-vault" className="btn-primary h-10 px-5 inline-flex items-center gap-2 mt-4 text-sm"><Coins size={15} /> Sell anonymized in My Vault</a>
        </div>
      )}

      <div className="rud-card rounded-2xl p-6 min-h-[280px]" data-testid="feed-panel">
        <h3 className="font-serif text-2xl mb-4 flex items-center gap-2"><Bell size={20} className="text-brand" /> My Kind Feed</h3>
        {feed.length === 0 ? (
          <div className="h-40 grid place-items-center text-center text-sec text-sm">Connect accounts and generate your feed.</div>
        ) : (
          <div className="space-y-3">
            {feed.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="rounded-lg bg-elevated border border-rud p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${f.type === "notification" ? "bg-blue-500/15 text-blue-300" : "bg-brand text-black"}`}>{f.type}</span>
                  <span className="text-xs text-sec">{f.provider}</span>
                </div>
                <div className="text-sm font-medium">{f.title}</div>
                <div className="text-xs text-sec mt-0.5">{f.detail}</div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Regimen({ user, setUser }) {
  const { lang } = useApp();
  const fileRef = useRef(null);
  const [f, setF] = useState({ goal: "", age: "", sex: "", blood_group: "", activity: "", diet_type: "", meals_per_day: "", junk_freq: "", packaged_snacks: "", packaged_drinks: "", alcohol: "", smoking: "", tobacco: "", drugs: "", conditions: "" });
  const [nonveg, setNonveg] = useState([]);
  const [devices, setDevices] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [showVitals, setShowVitals] = useState(true);

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));
  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const DEVICES = ["Apple Watch", "Fitbit", "Google Fit", "Samsung Health", "Oura Ring", "Garmin"];
  const NONVEG = ["Fish", "Chicken", "Eggs", "Beef", "Pork", "Mutton"];
  const FREQ = ["", "Never", "Rarely", "Weekly", "Few times/week", "Daily"];

  const run = async () => {
    if (!f.goal.trim()) { toast.error("Tell My Kind your health goal first."); return; }
    setLoading(true); setRes(null);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("nonveg_types", nonveg.join(", "));
      fd.append("devices", devices.join(", "));
      fd.append("lang", lang);
      if (file) fd.append("file", file);
      const { data } = await api.post("/personal/regimen", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(data.result);
      if (data.credits_remaining != null && user) setUser({ ...user, credits: data.credits_remaining });
      toast.success("Your personalized regimen is ready");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div className="rud-card rounded-2xl p-4 mt-6" data-testid="vitals-launcher">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-full bg-brand/15 text-brand grid place-items-center"><Activity size={18} /></span>
            <div>
              <div className="font-medium text-sm flex items-center gap-2">Live Vitals <span className="text-[10px] uppercase tracking-widest bg-brand/15 text-brand rounded-full px-2 py-0.5">from your phone</span></div>
              <div className="text-[11px] text-sec">Steps, camera heart-rate, Bluetooth monitors & a quick vitals log. Awareness only — not a diagnosis.</div>
            </div>
          </div>
          <button data-testid="toggle-live-vitals" onClick={() => setShowVitals((v) => !v)}
            className="btn-primary rounded-full h-9 px-5 text-sm">{showVitals ? "Hide Live Vitals" : "See Live Vitals"}</button>
        </div>
        {showVitals && <div className="mt-4"><LiveVitals /></div>}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
      <div className="rud-card rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1"><HeartPulse className="text-brand" /><h3 className="font-serif text-2xl">Your Health Regimen</h3></div>
        <p className="text-sec text-sm mb-4">A complete personalized plan — condition summary, exercise, nutrition, meal plan, breathwork, daily routine, goal advice & safety cautions. (10 credits)</p>

        <label className="text-xs uppercase tracking-widest text-sec block mb-1.5">Connect a health app / device</label>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {DEVICES.map((d) => (
            <button key={d} data-testid={`device-${d.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => { toggle(devices, setDevices, d); toast.info("Direct device sync is coming soon — we're building it now. Meanwhile, tap “See Live Vitals” to track heart-rate, steps & more straight from your phone."); }}
              className={`text-[11px] rounded-full px-3 py-1.5 border transition-colors ${devices.includes(d) ? "bg-brand text-black border-brand" : "border-rud text-sec hover:border-brand"}`}>{d}</button>
          ))}
        </div>

        <label className="text-xs uppercase tracking-widest text-sec block">Your health goal</label>
        <textarea data-testid="regimen-goal" value={f.goal} onChange={set("goal")} rows={2}
          placeholder="e.g. Lose weight & boost energy, manage blood sugar, build stamina, sleep better…"
          className="rud-input w-full rounded-lg px-3 py-2 mt-2 text-sm resize-none" />

        <div className="grid grid-cols-2 gap-2 mt-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Age</label>
            <input data-testid="regimen-age" value={f.age} onChange={set("age")} placeholder="e.g. 34" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Sex</label>
            <select data-testid="regimen-sex" value={f.sex} onChange={set("sex")} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              <option value="">Prefer not to say</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Blood group</label>
            <select data-testid="regimen-blood-group" value={f.blood_group} onChange={set("blood_group")} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              <option value="">Select…</option>
              {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((bg) => <option key={bg} value={bg}>{bg}</option>)}
              <option value="unknown">Don't know</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Activity</label>
            <select data-testid="regimen-activity" value={f.activity} onChange={set("activity")} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              <option value="">Select…</option><option value="sedentary">Sedentary</option><option value="light">Lightly active</option><option value="moderate">Moderately active</option><option value="very">Very active</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Meals / day</label>
            <select data-testid="regimen-meals" value={f.meals_per_day} onChange={set("meals_per_day")} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              <option value="">Select…</option>{["1","2","3","4","5","6"].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Diet type</label>
            <select data-testid="regimen-diettype" value={f.diet_type} onChange={set("diet_type")} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              <option value="">Select…</option><option value="Vegetarian">Vegetarian</option><option value="Vegan">Vegan</option><option value="Eggetarian">Eggetarian</option><option value="Non-vegetarian">Non-vegetarian</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Junk food</label>
            <select data-testid="regimen-junk" value={f.junk_freq} onChange={set("junk_freq")} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              {FREQ.map((v) => <option key={v} value={v}>{v || "Select…"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Packaged snacks</label>
            <select data-testid="regimen-snacks" value={f.packaged_snacks} onChange={set("packaged_snacks")} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              {FREQ.map((v) => <option key={v} value={v}>{v || "Select…"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Packaged drinks</label>
            <select data-testid="regimen-drinks" value={f.packaged_drinks} onChange={set("packaged_drinks")} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              {FREQ.map((v) => <option key={v} value={v}>{v || "Select…"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Alcohol</label>
            <select data-testid="regimen-alcohol" value={f.alcohol} onChange={set("alcohol")} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              {FREQ.map((v) => <option key={v} value={v}>{v || "Select…"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Smoking</label>
            <select data-testid="regimen-smoking" value={f.smoking} onChange={set("smoking")} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              {FREQ.map((v) => <option key={v} value={v}>{v || "Select…"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Tobacco</label>
            <select data-testid="regimen-tobacco" value={f.tobacco} onChange={set("tobacco")} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              {FREQ.map((v) => <option key={v} value={v}>{v || "Select…"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-sec block mb-1">Recreational drugs</label>
            <select data-testid="regimen-drugs" value={f.drugs} onChange={set("drugs")} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              {FREQ.map((v) => <option key={v} value={v}>{v || "Select…"}</option>)}
            </select>
          </div>
        </div>

        {(f.diet_type === "Non-vegetarian" || f.diet_type === "Eggetarian") && (
          <div className="mt-3">
            <label className="text-xs uppercase tracking-widest text-sec block mb-1.5">Which non-veg do you eat?</label>
            <div className="flex flex-wrap gap-1.5">
              {NONVEG.map((n) => (
                <button key={n} data-testid={`nonveg-${n.toLowerCase()}`} onClick={() => toggle(nonveg, setNonveg, n)}
                  className={`text-[11px] rounded-full px-3 py-1.5 border transition-colors ${nonveg.includes(n) ? "bg-brand text-black border-brand" : "border-rud text-sec hover:border-brand"}`}>{n}</button>
              ))}
            </div>
          </div>
        )}

        <label className="text-xs uppercase tracking-widest text-sec block mt-3 mb-1">Conditions / notes (optional)</label>
        <textarea data-testid="regimen-conditions" value={f.conditions} onChange={set("conditions")} rows={2}
          placeholder="e.g. Mild hypertension, knee pain, lactose intolerant…"
          className="rud-input w-full rounded-lg px-3 py-2 text-sm resize-none" />

        <input ref={fileRef} type="file" className="hidden" data-testid="regimen-file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.docx"
          onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <button data-testid="regimen-upload" onClick={() => fileRef.current?.click()} className="btn-ghost rounded-full w-full h-10 mt-3 flex items-center justify-center gap-2 text-sm">
          <Upload size={15} /> {file ? file.name : "Upload health report / scan (optional)"}
          {file && <span onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-sec hover:text-red-400"><X size={14} /></span>}
        </button>

        <button data-testid="regimen-run" onClick={run} disabled={loading} className="btn-primary w-full h-11 mt-3 flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <><Loader2 size={18} className="animate-spin" /> Building your regimen…</> : <><Wand2 size={16} /> Build my regimen</>}
        </button>

        <div className="flex items-start gap-2 rounded-lg bg-elevated border border-rud p-3 text-[11px] text-sec mt-4">
          <AlertTriangle size={14} className="shrink-0 mt-0.5 text-[var(--rud-text)]" /> General wellness guidance only — not medical advice. Consult your physician before starting any new exercise, diet or supplement.
        </div>
      </div>

      <div className="rud-card rounded-2xl p-6 min-h-[320px]">
        <AnimatePresence mode="wait">
          {!res && !loading && (
            <motion.div key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full grid place-items-center text-center text-sec">
              <div><HeartPulse size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">Your personalized health regimen will appear here.</p></div>
            </motion.div>
          )}
          {loading && (
            <motion.div key="l" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full grid place-items-center text-sec">
              <div className="text-center"><HeartLoader /><p className="text-sm">Designing your plan…</p></div>
            </motion.div>
          )}
          {res && (
            <motion.div key="r" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="regimen-result">
              {res.condition_summary && <div className="rounded-lg bg-elevated border border-rud p-3"><div className="text-xs uppercase tracking-widest text-brand mb-1">Condition summary</div><p className="text-sm leading-relaxed">{res.condition_summary}</p></div>}
              {res.profile_summary && <p className="text-sm leading-relaxed">{res.profile_summary}</p>}
              <Block icon={Dumbbell} title="Exercise" items={res.exercise} />
              <Block icon={Flower2} title="Yoga & asanas" items={res.yoga} />
              <Block icon={Salad} title="Food habits" items={res.food_habits} />
              <Block icon={Utensils} title="Nutrition" items={res.nutrition} />
              <Block icon={Apple} title="Fruits to eat" items={res.fruits} />
              <Block icon={Carrot} title="Vegetables to eat" items={res.vegetables} />
              {res.meal_plan?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><Utensils size={14} /> Meal plan</div>
                  <div className="space-y-1.5">{res.meal_plan.map((m, i) => (
                    <div key={i} className="rounded-lg bg-elevated border border-rud p-2.5"><div className="text-xs text-brand">{m.meal}</div><div className="text-sm">{m.suggestion}</div></div>
                  ))}</div>
                </div>
              )}
              <Block icon={Pill} title="Supplements (general)" items={res.supplements} />
              <Block icon={Wind} title="Breathwork" items={res.breathwork} />
              <Block icon={Shirt} title="What to wear" items={res.what_to_wear} />
              <Block icon={Clock} title="Daily routine" items={res.daily_routine} />
              <Block icon={Wand2} title="Advice for your goal" items={res.goal_advice} />
              {res.warnings?.length > 0 && (
                <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-red-400 mb-2"><ShieldAlert size={14} /> Cautions & warnings</div>
                  <ul className="space-y-1.5">{res.warnings.map((w, i) => <li key={i} className="text-sm text-red-300 flex gap-2"><span className="mt-1">·</span>{w}</li>)}</ul>
                </div>
              )}
              {res.disclaimer && <div className="flex gap-2 rounded-lg bg-elevated border border-rud p-3 text-xs text-sec"><AlertTriangle size={16} className="shrink-0 text-[var(--rud-text)]" /> {res.disclaimer}</div>}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
    </>
  );
}

function Block({ icon: I, title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><I size={14} /> {title}</div>
      <ul className="space-y-1.5">{items.map((it, i) => <li key={i} className="text-sm text-sec flex gap-2"><span className="text-brand mt-1">·</span>{it}</li>)}</ul>
    </div>
  );
}


const NEARBY_CATS = [
  { id: "restaurants", label: "Restaurants", icon: Utensils },
  { id: "parks", label: "Parks", icon: TreePine },
  { id: "gym", label: "Gym", icon: Dumbbell },
  { id: "art", label: "Art", icon: Sparkles },
  { id: "museum", label: "Museum", icon: BookOpen },
  { id: "libraries", label: "Libraries", icon: BookOpen },
  { id: "attractions", label: "Attractions", icon: MapPin },
  { id: "grocery", label: "Grocery", icon: Store },
  { id: "mall", label: "Mall", icon: Store },
  { id: "petrol", label: "Petrol / Gas", icon: Fuel },
  { id: "chemists", label: "Chemists", icon: Pill },
  { id: "hospitals", label: "Hospitals", icon: Cross },
  { id: "salons", label: "Salons", icon: Sparkles },
  { id: "post", label: "Post", icon: Mail },
  { id: "parking", label: "Parking", icon: Car },
  { id: "drycleaning", label: "Dry cleaning", icon: Shirt },
  { id: "banks", label: "Banks", icon: Coins },
  { id: "atms", label: "ATMs", icon: Coins },
];

function Nearby() {
  const { lang } = useApp();
  const [city, setCity] = useState("");
  const [coords, setCoords] = useState(null);
  const [locBusy, setLocBusy] = useState(false);
  const [interests, setInterests] = useState("");
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [suggests, setSuggests] = useState([]);
  const [sugOpen, setSugOpen] = useState(false);
  const [ratings, setRatings] = useState({});
  const sugTimer = useRef(null);
  const skipSearch = useRef(false);

  const toggle = (id) => setCats((prev) => prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]);

  const onCityChange = (val) => {
    setCity(val);
    if (skipSearch.current) { skipSearch.current = false; return; }
    clearTimeout(sugTimer.current);
    if (val.trim().length < 3) { setSuggests([]); setSugOpen(false); return; }
    sugTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=6&q=${encodeURIComponent(val)}`, { headers: { "Accept-Language": "en" } });
        const d = await r.json();
        setSuggests(Array.isArray(d) ? d : []); setSugOpen(true);
      } catch { setSuggests([]); }
    }, 350);
  };
  const pickSuggest = (sug) => {
    skipSearch.current = true;
    setCity(sug.display_name);
    setCoords({ lat: parseFloat(sug.lat), lon: parseFloat(sug.lon) });
    setSuggests([]); setSugOpen(false);
    toast.success("Precise location set");
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported on this device."); return; }
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      setCoords({ lat: latitude, lon: longitude });
      try {
        const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
        const d = await r.json();
        const label = [d.locality || d.city, d.principalSubdivision, d.countryName].filter(Boolean).join(", ");
        skipSearch.current = true;
        if (label) setCity(label);
        toast.success("Using your precise location");
      } catch { toast.success("Location captured"); }
      finally { setLocBusy(false); }
    }, () => { toast.error("Couldn't get your location. Please type your area."); setLocBusy(false); }, { enableHighAccuracy: true, timeout: 10000 });
  };

  const run = async () => {
    if (!city.trim() && !coords) { toast.error("Use my location or type your area."); return; }
    if (cats.length === 0) { toast.error("Pick at least one category."); return; }
    setLoading(true); setRes(null); setSugOpen(false);
    try {
      const { data } = await api.post("/personal/nearby", { city, interests, categories: cats, lat: coords?.lat, lon: coords?.lon, lang });
      const result = data.result;
      setRes(result);
      const keys = [];
      cats.forEach((c) => (result.places?.[c] || []).forEach((p) => keys.push(`${p.name}@${city}`)));
      if (keys.length) { try { const rr = await api.post("/personal/nearby/ratings", { keys }); setRatings(rr.data.ratings || {}); } catch { /* ignore */ } }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6" data-testid="nearby-panel">
      <div className="rud-card rounded-2xl p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-4"><MapPin size={14} /> What's nearby</div>
        <div className="flex flex-wrap gap-2 items-center">
          <button data-testid="nearby-use-location" onClick={useMyLocation} disabled={locBusy} className="btn-ghost rounded-full h-10 px-4 text-sm flex items-center gap-1.5 disabled:opacity-50">
            {locBusy ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />} Use my precise location
          </button>
          {coords && <span className="text-[11px] text-brand" data-testid="nearby-coords">📍 located</span>}
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div className="relative">
            <input data-testid="nearby-city" value={city} onChange={(e) => onCityChange(e.target.value)} onFocus={() => suggests.length && setSugOpen(true)}
              placeholder="Type an address or area — pick from suggestions" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" autoComplete="off" />
            {sugOpen && suggests.length > 0 && (
              <div data-testid="nearby-suggestions" className="absolute z-30 left-0 right-0 mt-1 rounded-xl border border-rud bg-[var(--rud-surface)] shadow-2xl max-h-64 overflow-y-auto">
                {suggests.map((sug, i) => (
                  <button key={i} data-testid={`nearby-suggest-${i}`} onClick={() => pickSuggest(sug)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-brand/10 flex items-start gap-2 border-b border-rud last:border-0">
                    <MapPin size={13} className="text-brand shrink-0 mt-0.5" /> <span className="text-[var(--rud-text)]">{sug.display_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <input data-testid="nearby-interests" value={interests} onChange={(e) => setInterests(e.target.value)} placeholder="Preferences (optional — veg, budget, family…)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-sec">Choose what to explore <span className="text-[var(--rud-text)]">({cats.length} selected)</span></div>
            {cats.length > 0 && <button data-testid="nearby-clear-cats" onClick={() => setCats([])} className="text-[11px] text-sec hover:text-brand">Clear</button>}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="nearby-categories">
            {NEARBY_CATS.map((c) => {
              const on = cats.includes(c.id);
              return (
                <button key={c.id} data-testid={`nearby-cat-${c.id}`} aria-pressed={on} onClick={() => toggle(c.id)} className={`flex items-center gap-1.5 text-sm rounded-full px-3.5 py-1.5 border transition-colors ${on ? "border-brand bg-brand text-black font-semibold shadow-[0_0_0_2px_rgba(0,0,0,0.15)_inset]" : "border-rud text-sec hover:border-brand hover:text-[var(--rud-text)]"}`}>
                  {on ? <CheckCircle2 size={14} /> : <c.icon size={14} />} {c.label}
                </button>
              );
            })}
          </div>
        </div>
        <button data-testid="nearby-run" onClick={run} disabled={loading} className="btn-primary h-11 px-6 mt-5 flex items-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <SearchIcon size={16} />} Find nearby
        </button>
      </div>

      {res && (
        <div className="mt-5 space-y-4" data-testid="nearby-results">
          {res.advice && (
            <div className="rud-card rounded-2xl p-5 flex items-start gap-3" data-testid="nearby-advice">
              <CloudSun size={20} className="text-brand shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium">What to wear & carry</div>
                <p className="text-sm text-sec mt-0.5">{res.advice}</p>
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            {res.weather && (
              <div className="rud-card rounded-2xl p-5" data-testid="nearby-weather">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><CloudSun size={14} /> Weather</div>
                <p className="text-sm text-sec">{res.weather}</p>
              </div>
            )}
            {res.aqi && (
              <div className="rud-card rounded-2xl p-5" data-testid="nearby-aqi">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><Wind size={14} /> Air quality</div>
                <p className="text-sm text-sec">{res.aqi}</p>
              </div>
            )}
          </div>
          {res.places && NEARBY_CATS.filter((c) => res.places[c.id]?.length).map((c) => (
            <div key={c.id} className="rud-card rounded-2xl p-6" data-testid={`nearby-list-${c.id}`}>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><c.icon size={14} /> {c.label}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {res.places[c.id].map((p, i) => (
                  <PlaceCard key={i} place={p} cityKey={city} agg={ratings[`${p.name}@${city}`]}
                    onReviewed={(k, a) => setRatings((prev) => ({ ...prev, [k]: a }))} />
                ))}
              </div>
            </div>
          ))}
          {res.note && <div className="flex gap-2 rounded-lg bg-elevated border border-rud p-3 text-xs text-sec"><AlertTriangle size={16} className="shrink-0" /> {res.note} · Guidance is illustrative, not live data.</div>}
        </div>
      )}
    </motion.div>
  );
}

function Stars({ value = 0, size = 14 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= Math.round(value);
        return <Star key={n} size={size} className={filled ? "text-brand" : "text-sec"} style={{ fill: filled ? "currentColor" : "none" }} />;
      })}
    </span>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={`star-${n}`} onClick={() => onChange(n)} className="p-0.5">
          <Star size={22} className={n <= value ? "text-brand" : "text-sec hover:text-brand"} style={{ fill: n <= value ? "currentColor" : "none" }} />
        </button>
      ))}
    </div>
  );
}

function PlaceCard({ place, cityKey, agg, onReviewed }) {
  const { user } = useApp();
  const key = `${place.name}@${cityKey}`;
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(agg || null);
  useEffect(() => { setLocal(agg || null); }, [agg]);

  const submit = async () => {
    if (!user) { toast.error("Sign in to write a review."); return; }
    if (!rating) { toast.error("Pick a star rating."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/personal/nearby/review", { place_key: key, place_name: place.name, rating, text });
      const a = { avg: data.avg, count: data.count };
      setLocal(a); onReviewed?.(key, a);
      toast.success("Thanks for your review!"); setOpen(false); setText("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const avg = local?.avg || 0, count = local?.count || 0;
  return (
    <div className="rounded-lg bg-elevated border border-rud p-3" data-testid="nearby-place">
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium">{place.name}</div>
        <Stars value={avg} size={12} />
      </div>
      {place.note && <div className="text-xs text-sec mt-0.5">{place.note}</div>}
      <div className="mt-1.5 space-y-1 text-[11px] text-sec">
        {place.address && <div className="flex items-start gap-1.5"><MapPin size={12} className="text-brand shrink-0 mt-0.5" /> {place.address}</div>}
        {place.hours && <div className="flex items-start gap-1.5"><Clock size={12} className="text-brand shrink-0 mt-0.5" /> {place.hours}</div>}
        {place.phone && <div className="flex items-center gap-1.5"><Phone size={12} className="text-brand shrink-0" /> <a href={`tel:${place.phone}`} className="hover:text-brand">{place.phone}</a></div>}
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-rud">
        <span className="text-[11px] text-sec">{count ? `${avg}★ · ${count} review${count > 1 ? "s" : ""}` : "No reviews yet"}</span>
        <button data-testid="write-review-toggle" onClick={() => setOpen((o) => !o)} className="text-[11px] text-brand hover:underline">Write review</button>
      </div>
      {open && (
        <div className="mt-2">
          <StarPicker value={rating} onChange={setRating} />
          <textarea data-testid="review-text" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Share your experience (optional)" className="rud-input w-full rounded-lg px-2.5 py-1.5 text-xs mt-2" />
          <button data-testid="submit-review" onClick={submit} disabled={busy} className="btn-primary rounded-full h-8 px-4 text-xs mt-2 flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Star size={12} />} Post review
          </button>
        </div>
      )}
    </div>
  );
}

function MyHabits({ scope = "personal" }) {
  const pro = scope === "professional";
  const { user, refreshUser } = useApp();
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [isHealth, setIsHealth] = useState(false);
  const [book, setBook] = useState("");
  const [sugg, setSugg] = useState([]);
  const [busy, setBusy] = useState(false);
  const [board, setBoard] = useState([]);

  const load = async () => { try { const { data } = await api.get("/habits", { params: { scope } }); setData(data); } catch (e) { /* */ } };
  useEffect(() => { if (user) { load(); api.get("/habits/leaderboard").then(r => setBoard(r.data.leaderboard || [])).catch(() => {}); } }, [user, scope]);
  if (!user) return <div className="rud-card rounded-2xl p-8 text-center text-sec mt-6" data-testid={pro ? "my-habits-professional" : "my-habits"}>Sign in to build and track your {pro ? "professional" : ""} habits.</div>;

  const add = async (t, cat, source) => { try { await api.post("/habits", { title: t, category: cat, source: source || "Manual" }); toast.success("Habit added"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const suggest = async () => { if (!book.trim()) return; setBusy(true); try { const { data } = await api.post("/habits/suggest", { book_title: book, scope }); setSugg(data.habits || []); if (!(data.habits || []).length) toast("No habits found — try another title."); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const checkin = async (h) => { try { const { data } = await api.post(`/habits/${h.id}/checkin`, {}); if (data.already) toast("Already checked in today ✓"); else { toast.success(`Streak ${data.streak}🔥${data.vault_reward ? ` · +${data.vault_reward} vault credits!` : ""}`); if (data.vault_reward) refreshUser?.(); } load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const del = async (id) => { await api.delete(`/habits/${id}`); load(); };
  const setGoal = async (h) => { const g = window.prompt(`Set a goal for "${h.title}"` + (h.category === "health" ? "\n(Health: e.g. sleep 7h / 8k steps)" : h.category === "professional" ? "\n(e.g. ship 1 feature/week, 2 deep-work hrs/day)" : ""), h.goal || ""); if (g == null) return; try { const { data } = await api.post(`/habits/${h.id}/goal`, { goal: g, answers: [] }); toast.success(data.regimen ? "Goal set — plan added!" : "Goal set"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const claim = async () => { try { const { data } = await api.post("/habits/vault/claim", {}); toast.success(`Claimed ${data.claimed} credits from your Habit Vault!`); refreshUser?.(); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const habits = data?.habits || [];
  return (
    <div className="mt-6 space-y-4" data-testid={pro ? "my-habits-professional" : "my-habits"}>
      {/* Vault + streak */}
      {pro ? (
        <div className="rud-card rounded-2xl p-5 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-brand/15 grid place-items-center text-brand shrink-0"><Flame size={20} /></div>
          <div><div className="text-[11px] uppercase tracking-widest text-sec">Professional streak</div><div className="font-serif text-2xl">{data?.total_streak ?? 0} days</div></div>
        </div>
      ) : (
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rud-card rounded-2xl p-5 flex items-center gap-3" data-testid="habit-vault">
          <div className="w-11 h-11 rounded-xl bg-brand/15 grid place-items-center text-brand shrink-0"><Trophy size={20} /></div>
          <div className="flex-1"><div className="text-[11px] uppercase tracking-widest text-sec">Habit Vault</div><div className="font-serif text-2xl">{data?.vault ?? 0} <span className="text-sm text-sec">credits</span></div></div>
          <button data-testid="habit-vault-claim" onClick={claim} className="btn-primary rounded-full h-9 px-4 text-sm">Claim</button>
        </div>
        <div className="rud-card rounded-2xl p-5 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-brand/15 grid place-items-center text-brand shrink-0"><Flame size={20} /></div>
          <div><div className="text-[11px] uppercase tracking-widest text-sec">Total streak</div><div className="font-serif text-2xl">{data?.total_streak ?? 0} days</div></div>
        </div>
      </div>
      )}
      {!pro && !data?.has_purchased && <p className="text-[11px] text-sec">💡 Healthy-habit streaks earn vault credits — they unlock the moment you make your first credit purchase.</p>}

      {/* Add + suggest */}
      <div className="rud-card rounded-2xl p-5">
        <div className="flex flex-wrap gap-2">
          <input data-testid="habit-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={pro ? "Add a professional habit (e.g. Inbox zero by 10am)" : "Add a habit (e.g. Drink 3L water)"} className="rud-input flex-1 min-w-[200px] rounded-lg px-3 py-2 text-sm" />
          {!pro && <label className="flex items-center gap-1.5 text-xs text-sec"><input type="checkbox" checked={isHealth} onChange={(e) => setIsHealth(e.target.checked)} /> Health</label>}
          <button data-testid="habit-add" onClick={() => { if (title.trim()) { add(title, pro ? "professional" : (isHealth ? "health" : "general")); setTitle(""); } }} className="btn-primary rounded-lg h-9 px-4 text-sm">Add</button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <input data-testid="habit-book" value={book} onChange={(e) => setBook(e.target.value)} placeholder={pro ? "Get habits from a book — e.g. Deep Work, Atomic Habits" : "Get habits from a book — e.g. Atomic Habits"} className="rud-input flex-1 min-w-[200px] rounded-lg px-3 py-2 text-sm" />
          <button data-testid="habit-suggest" onClick={suggest} disabled={busy} className="btn-outline rounded-lg h-9 px-4 text-sm flex items-center gap-1.5">{busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Suggest</button>
        </div>
        {sugg.length > 0 && (
          <div className="mt-3 space-y-2">
            {sugg.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-sm bg-elevated border border-rud rounded-lg px-3 py-2">
                <div className="flex-1"><b>{s.title}</b>{s.why && <span className="text-sec"> — {s.why}</span>}</div>
                <button data-testid={`habit-add-sugg-${i}`} onClick={() => add(s.title, pro ? "professional" : (s.category === "health" ? "health" : "general"), book)} className="text-brand text-xs flex items-center gap-1"><Plus size={13} /> Add</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Habit list */}
      {habits.length === 0 ? <p className="text-sec text-sm">No {pro ? "professional " : ""}habits yet — add one above or pull them from a book.</p> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {habits.map((h) => {
            const doneToday = h.last_checkin === new Date().toISOString().slice(0, 10);
            return (
              <div key={h.id} className="rud-card rounded-2xl p-4" data-testid={`habit-${h.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><div className="font-medium text-sm">{h.title}</div><div className="text-[11px] text-sec">{h.source}{h.category === "health" ? " · Health" : h.category === "professional" ? " · Professional" : ""}</div></div>
                  <button onClick={() => del(h.id)} className="text-sec hover:text-red-400"><Trash2 size={14} /></button>
                </div>
                {h.goal && <div className="text-[11px] text-brand mt-1">🎯 {h.goal}</div>}
                {h.regimen && <div className="text-[11px] text-sec mt-1 whitespace-pre-wrap line-clamp-4">{h.regimen}</div>}
                <div className="flex items-center gap-3 mt-3 pt-2 border-t border-rud">
                  <span className="text-xs text-sec flex items-center gap-1"><Flame size={13} className="text-brand" /> {h.streak}d · best {h.best_streak}</span>
                  <button onClick={() => setGoal(h)} className="text-[11px] text-sec hover:text-brand ml-auto">Set goal</button>
                  <button data-testid={`habit-checkin-${h.id}`} onClick={() => checkin(h)} disabled={doneToday} className={`rounded-full h-8 px-3 text-xs flex items-center gap-1 ${doneToday ? "bg-brand/20 text-brand" : "btn-primary"}`}><CheckCircle2 size={13} /> {doneToday ? "Done today" : "Check in"}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Leaderboard */}
      {!pro && board.length > 0 && (
        <div className="rud-card rounded-2xl p-5" data-testid="habit-leaderboard">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><Trophy size={14} /> Streak leaderboard</div>
          {board.slice(0, 8).map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-sm py-1"><span className="text-sec w-5">{i + 1}</span><span className="flex-1 truncate">{r.name}</span><span className="text-brand flex items-center gap-1"><Flame size={12} /> {r.streak}d</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

function Carbon() {
  const { lang, countryObj } = useApp();
  const [scope, setScope] = useState("individual");
  const [f, setF] = useState({ country: countryObj?.name || "", city: "", car_type: "", km_driven: "", km_period: "monthly", flights_yearly: "", home_size: "", people: "", appliances: "", diet: "", electricity: "", sleep_hours: "", wake_time: "", employees: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  // Intelligent organization flow
  const [org, setOrg] = useState({ industry: "", org_size: "", facilities: "", employees: "", country: countryObj?.name || "", notes: "" });
  const setO = (k) => (e) => setOrg((p) => ({ ...p, [k]: e.target.value }));
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({});
  const [qLoading, setQLoading] = useState(false);

  const run = async () => {
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/carbon/estimate", { ...f, scope, lang });
      setRes(data.result);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const genQuestions = async () => {
    if (!org.industry.trim()) { toast.error("Tell us your industry / sector first."); return; }
    setQLoading(true); setRes(null); setQuestions(null); setAnswers({});
    try {
      const { data } = await api.post("/carbon/org-questions", { ...org, lang });
      setQuestions(data);
      if (!data.questions?.length) toast("No questions generated — try adding more detail.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setQLoading(false); }
  };

  const estimateOrg = async () => {
    const ans = (questions?.questions || []).map((q) => ({ id: q.id, label: q.label, answer: answers[q.id] || "" }));
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/carbon/org-estimate", { ...org, answers: ans, lang });
      setRes(data.result);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const switchScope = (sc) => { setScope(sc); setRes(null); setQuestions(null); };
  const scopeColor = { "1": "text-orange-400 border-orange-400/40", "2": "text-sky-400 border-sky-400/40", "3": "text-emerald-400 border-emerald-400/40" };

  const [dl, setDl] = useState(false);
  const downloadReport = async () => {
    setDl(true);
    try {
      const r = await api.post("/carbon/report", { result: res, scope, org }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "My Kind-Carbon-Report.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Branded carbon report downloaded");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't build the report."); }
    finally { setDl(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6" data-testid="carbon-panel">
      <div className="rud-card rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Leaf size={14} /> Carbon Footprint</div>
          <div className="flex gap-1 p-0.5 rounded-full bg-elevated border border-rud text-[11px]">
            <button data-testid="carbon-scope-individual" onClick={() => switchScope("individual")} className={`px-3 py-1 rounded-full transition-colors ${scope === "individual" ? "bg-brand text-black" : "text-sec"}`}>Individual</button>
            <button data-testid="carbon-scope-org" onClick={() => switchScope("organization")} className={`px-3 py-1 rounded-full transition-colors ${scope === "organization" ? "bg-brand text-black" : "text-sec"}`}>Organization</button>
          </div>
        </div>

        {scope === "individual" ? (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="carbon-country" value={f.country} onChange={set("country")} placeholder="Country" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="carbon-city" value={f.city} onChange={set("city")} placeholder="City" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="carbon-car" value={f.car_type} onChange={set("car_type")} placeholder="Car / fuel type (e.g. petrol SUV, EV, none)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <div className="flex gap-2">
                <input data-testid="carbon-km" value={f.km_driven} onChange={set("km_driven")} placeholder="Km driven" className="rud-input flex-1 rounded-lg px-3 py-2.5 text-sm" />
                <select data-testid="carbon-km-period" value={f.km_period} onChange={set("km_period")} className="rud-input rounded-lg px-2 py-2.5 text-sm"><option value="daily">/day</option><option value="monthly">/month</option><option value="yearly">/year</option></select>
              </div>
              <input data-testid="carbon-flights" value={f.flights_yearly} onChange={set("flights_yearly")} placeholder="Flights per year" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="carbon-home" value={f.home_size} onChange={set("home_size")} placeholder="Home size (e.g. 1200 sq ft / 2BHK)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="carbon-people" value={f.people} onChange={set("people")} placeholder="People in household" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="carbon-appliances" value={f.appliances} onChange={set("appliances")} placeholder="Major appliances (AC, geyser, fridge…)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="carbon-electricity" value={f.electricity} onChange={set("electricity")} placeholder="Electricity (units/month or bill)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="carbon-diet" value={f.diet} onChange={set("diet")} placeholder="Diet (veg / non-veg / vegan)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="carbon-sleep" value={f.sleep_hours} onChange={set("sleep_hours")} placeholder="Sleep hours" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="carbon-wake" value={f.wake_time} onChange={set("wake_time")} placeholder="Usual wake time" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
            </div>
            <textarea data-testid="carbon-notes" value={f.notes} onChange={set("notes")} rows={2} placeholder="Anything else? (optional)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-3 resize-none" />
            <button data-testid="carbon-run" onClick={run} disabled={loading} className="btn-primary h-11 px-6 mt-4 flex items-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Leaf size={16} />} Estimate my footprint
            </button>
          </>
        ) : (
          <>
            <p className="text-xs text-sec mb-3">Tell us the basics — My Kind generates a smart questionnaire tailored to your industry, size, sites &amp; headcount, then estimates your GHG Protocol footprint.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="orgc-industry" value={org.industry} onChange={setO("industry")} placeholder="Industry / sector (e.g. cement, SaaS, hotel)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="orgc-size" value={org.org_size} onChange={setO("org_size")} placeholder="Size (e.g. SME, ~$5M revenue, large)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="orgc-facilities" value={org.facilities} onChange={setO("facilities")} placeholder="Number of facilities / sites" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="orgc-employees" value={org.employees} onChange={setO("employees")} placeholder="Number of employees" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="orgc-country" value={org.country} onChange={setO("country")} placeholder="Country" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="orgc-notes" value={org.notes} onChange={setO("notes")} placeholder="Anything notable? (optional)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
            </div>
            <button data-testid="orgc-generate" onClick={genQuestions} disabled={qLoading} className="btn-primary h-11 px-6 mt-4 flex items-center gap-2 disabled:opacity-60">
              {qLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {questions ? "Regenerate questions" : "Generate smart questions"}
            </button>

            {questions && (
              <div className="mt-5 border-t border-rud pt-5" data-testid="orgc-questions">
                {questions.intro && <p className="text-sm text-sec mb-4">{questions.intro}</p>}
                <div className="space-y-3">
                  {questions.questions.map((q) => (
                    <div key={q.id} data-testid={`orgc-q-${q.id}`}>
                      <label className="text-sm flex items-center gap-2 mb-1">
                        <span className={`text-[9px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 ${scopeColor[q.scope] || "text-sec border-rud"}`}>S{q.scope}</span>
                        <span className="flex-1">{q.label}</span>
                        {q.unit && <span className="text-[10px] text-sec">({q.unit})</span>}
                      </label>
                      <input data-testid={`orgc-a-${q.id}`} value={answers[q.id] || ""} onChange={(e) => setAnswers((p) => ({ ...p, [q.id]: e.target.value }))}
                        placeholder={q.placeholder || "Your answer"} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
                    </div>
                  ))}
                </div>
                <button data-testid="orgc-estimate" onClick={estimateOrg} disabled={loading} className="btn-primary h-11 px-6 mt-5 flex items-center gap-2 disabled:opacity-60">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Leaf size={16} />} Get carbon footprint
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {res && (
        <div className="mt-5 space-y-4" data-testid="carbon-results">
          <div className="flex justify-end">
            <button data-testid="carbon-download-report" onClick={downloadReport} disabled={dl}
              className="btn-outline h-10 px-4 flex items-center gap-2 text-sm disabled:opacity-60">
              {dl ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download branded report (PDF)
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="rud-card rounded-2xl p-5 text-center">
              <div className="text-3xl font-semibold tabular-nums">{res.total_tonnes}</div>
              <div className="text-[11px] text-sec mt-1">tonnes CO₂e / year{res.per_employee_tonnes ? ` · ${res.per_employee_tonnes} t / employee` : ""}</div>
            </div>
            <div className="rud-card rounded-2xl p-5 text-center">
              <div className="text-3xl font-semibold tabular-nums flex items-center justify-center gap-1"><TreePine size={22} className="text-brand" /> {res.trees_to_plant}</div>
              <div className="text-[11px] text-sec mt-1">trees to offset</div>
            </div>
            <div className="rud-card rounded-2xl p-5">
              <div className="text-[11px] text-sec">Vs average</div>
              <div className="text-sm mt-1">{res.vs_average}</div>
            </div>
          </div>
          {res.breakdown?.length > 0 && (
            <div className="rud-card rounded-2xl p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><BarChart3 size={14} /> Breakdown</div>
              <div className="space-y-2">
                {res.breakdown.map((b, i) => (
                  <div key={i} className="flex justify-between text-sm border-b border-rud/50 pb-1.5">
                    <span>{b.category}<span className="text-sec text-xs ml-2">{b.note}</span></span>
                    <span className="tabular-nums font-medium">{b.tonnes} t</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <Block icon={Lightbulb} title="Path to net-zero" items={res.recommendations} />
            <Block icon={HeartPulse} title={scope === "organization" ? "Co-benefits" : "Health co-benefits"} items={res.health_cobenefits} />
          </div>
          <Block icon={Leaf} title="Offset options" items={res.offsets} />
          {res.disclaimer && <div className="flex gap-2 rounded-lg bg-elevated border border-rud p-3 text-xs text-sec"><AlertTriangle size={16} className="shrink-0" /> {res.disclaimer}</div>}
        </div>
      )}
    </motion.div>
  );
}

function CityInput({ value, onChange, placeholder, testid }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState([]);
  useEffect(() => { setQ(value || ""); }, [value]);
  const onType = (v) => {
    setQ(v); onChange(v);
    const list = searchCities(v);
    setOpts(list); setOpen(list.length > 0);
  };
  const pick = (c) => { setQ(c); onChange(c); setOpen(false); };
  return (
    <div className="relative">
      <input data-testid={testid} value={q} onChange={(e) => onType(e.target.value)} onFocus={() => q && setOpts(searchCities(q)) && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder={placeholder} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" autoComplete="off" />
      {open && opts.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-rud bg-elevated shadow-lg max-h-52 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {opts.map((c) => (
            <button key={c} type="button" data-testid={`${testid}-opt`} onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-brand/10 flex items-center gap-2"><MapPin size={13} className="text-brand shrink-0" /> {c}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function StayRecs() {
  const [recs, setRecs] = useState(null);
  useEffect(() => { api.get("/transport/stays/recommendations").then(({ data }) => setRecs(data)).catch(() => {}); }, []);
  if (!recs || !recs.recommendations?.length) return null;
  return (
    <div className="rud-card rounded-2xl p-6" data-testid="stay-recs">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><MapPin size={14} /> Stays for your travels</div>
      <p className="text-sec text-sm mb-4">Community homes matched to your upcoming trips and your interests, culture &amp; food.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {recs.recommendations.map((l) => (
          <a key={l.id} href={`/stays/${l.id}`} className="rud-card rounded-xl overflow-hidden hover:ring-1 hover:ring-brand/40 block" data-testid={`rec-${l.id}`}>
            {l.photos?.[0] ? <img src={l.photos[0]} alt="" className="w-full h-28 object-cover" /> : <div className="w-full h-28 bg-brand/10 grid place-items-center text-brand"><MapPin size={22} /></div>}
            <div className="p-3">
              <div className="font-medium text-sm truncate">{l.home_name}</div>
              <div className="text-[11px] text-sec truncate flex items-center gap-1"><MapPin size={10} /> {l.city_label}</div>
              {l.matched_destination && <div className="text-[11px] text-brand mt-1">Matches your trip: {l.matched_destination}</div>}
              {l.shared_interests?.length > 0 && <div className="text-[11px] text-brand">Shared: {l.shared_interests.slice(0, 3).join(", ")}</div>}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function Social({ user, setUser }) {
  const { refreshUser } = useApp();
  const [f, setF] = useState({ first_name: "", last_name: "", birth_place: "", home_town: "", dob: "", interests: "", bio: "", photo: "" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current || user === undefined) return;
    hydrated.current = true;
    const s = user?.social_profile; if (!s) return;
    setF({ first_name: s.first_name || "", last_name: s.last_name || "", birth_place: s.birth_place || "",
      home_town: s.home_town || "", dob: s.dob || "", interests: s.interests || "", bio: s.bio || "", photo: s.photo || "" });
  }, [user]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/social-profile", f);
      if (setUser && user) setUser({ ...user, social_profile: data.social_profile });
      if (refreshUser) refreshUser();
      setEditing(false);
      toast.success("Kind Social profile saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!user) return <div className="rud-card rounded-2xl p-6 mt-6 text-center text-sec text-sm">Sign in to create your Kind Social profile.</div>;
  const initials = `${(f.first_name[0] || "")}${(f.last_name[0] || "")}`.toUpperCase() || "🙂";
  const sp = user?.social_profile || {};
  const hasProfile = !!(sp.first_name || sp.last_name || sp.home_town || sp.bio || sp.photo);
  const showForm = !hasProfile || editing;
  const tags = (f.interests || "").split(",").map((s) => s.trim()).filter(Boolean);
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-5" data-testid="social-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-widest text-brand flex items-center gap-1.5"><Users size={14} /> Kind Social — your feed</div>
        <button data-testid="toggle-my-profile" onClick={() => setShowProfile((v) => !v)} className="btn-ghost rounded-full h-9 px-4 text-sm border border-rud">{showProfile ? "Hide my profile" : (hasProfile ? "My profile" : "Set up profile")}</button>
      </div>
      {showProfile && (
        showForm ? (
          <div className="rud-card rounded-2xl p-6" data-testid="social-edit-form">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-16 h-16 rounded-full bg-brand/15 grid place-items-center text-brand text-xl font-serif shrink-0 overflow-hidden">
                {f.photo ? <img src={f.photo} alt="profile" className="w-full h-full object-cover" data-testid="social-photo-preview" /> : initials}
              </div>
              <div className="min-w-0">
                <div className="font-serif text-2xl leading-tight">{[f.first_name, f.last_name].filter(Boolean).join(" ") || "Your name"}</div>
                <div className="text-sec text-sm">{f.home_town || "Add your home town"}</div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <FileUpload category="avatar" accept="image/*" label="Add photo" testId="social-photo-upload"
                  onUploaded={(file) => setF((s) => ({ ...s, photo: file.absoluteUrl }))} />
                {f.photo && <button data-testid="social-photo-remove" onClick={() => setF((s) => ({ ...s, photo: "" }))} className="btn-ghost rounded-lg h-9 px-3 text-sm flex items-center gap-1.5"><X size={14} /> Remove</button>}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="social-first-name" value={f.first_name} onChange={set("first_name")} placeholder="First name" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="social-last-name" value={f.last_name} onChange={set("last_name")} placeholder="Last name" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              <CityInput testid="social-birth-place" value={f.birth_place} onChange={(v) => setF((s) => ({ ...s, birth_place: v }))} placeholder="Birth city (type to search)" />
              <CityInput testid="social-home-town" value={f.home_town} onChange={(v) => setF((s) => ({ ...s, home_town: v }))} placeholder="Current city (type to search)" />
              <div>
                <label className="text-[11px] uppercase tracking-widest text-sec block mb-1">Date of birth</label>
                <input data-testid="social-dob" type="date" value={f.dob} onChange={set("dob")} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
              </div>
            </div>
            <input data-testid="social-interests" value={f.interests} onChange={set("interests")} placeholder="Interests (comma separated) — e.g. travel, cricket, music" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-3" />
            <textarea data-testid="social-bio" value={f.bio} onChange={set("bio")} rows={2} placeholder="A short bio (optional)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-3 resize-none" />
            {tags.length > 0 && <div className="flex flex-wrap gap-2 mt-3">{tags.map((tag, i) => (<span key={i} className="text-xs rounded-full bg-brand/10 text-brand px-3 py-1">{tag}</span>))}</div>}
            <div className="flex gap-2 mt-4">
              <button data-testid="social-save" onClick={save} disabled={busy} className="btn-primary h-11 px-6 flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />} Save profile</button>
              {hasProfile && <button data-testid="social-cancel-edit" onClick={() => setEditing(false)} className="btn-ghost h-11 px-4 rounded-lg text-sm border border-rud">Cancel</button>}
            </div>
          </div>
        ) : (
          <div className="rud-card rounded-2xl p-6" data-testid="social-profile-view">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-brand/15 grid place-items-center text-brand text-2xl font-serif shrink-0 overflow-hidden">
                {f.photo ? <img src={f.photo} alt="profile" className="w-full h-full object-cover" /> : initials}
              </div>
              <div className="min-w-0 flex-1">
                {f.home_town && <div className="text-sec text-sm flex items-center gap-1.5"><MapPin size={13} className="text-brand" /> {f.home_town}</div>}
              </div>
              <button data-testid="social-edit-profile" onClick={() => setEditing(true)} className="btn-ghost rounded-full h-9 px-4 text-sm border border-rud">Edit profile</button>
            </div>
            {f.bio && <p className="text-sm mt-4 leading-relaxed text-[var(--rud-text)]" data-testid="profile-view-bio">{f.bio}</p>}
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4 text-[13px] text-sec">
              {f.birth_place && <span>Born in <b className="text-[var(--rud-text)]">{f.birth_place}</b></span>}
              {f.dob && <span>DOB <b className="text-[var(--rud-text)]">{f.dob}</b></span>}
            </div>
            {tags.length > 0 && <div className="flex flex-wrap gap-2 mt-4" data-testid="profile-view-interests">{tags.map((tag, i) => (<span key={i} className="text-xs rounded-full bg-brand/10 text-brand px-3 py-1">{tag}</span>))}</div>}
          </div>
        )
      )}

      <div data-testid="social-feed-col">
        <KindFeed />
      </div>
      <StayRecs />
      <UserReports ownerId={user.user_id} isOwner title="Your Search & Ask reports" />
      <UserOutputs ownerId={user.user_id} isOwner category="personal" title="Your personal saved outputs — Save & Earn" />
    </motion.div>
  );
}

function Professional({ user, setUser }) {
  const { refreshUser } = useApp();
  const p = user?.professional || {};
  const photoRef = useRef(null);
  const [f, setF] = useState({
    headline: p.headline || "", summary: p.summary || "", location: p.location || "", photo: p.photo || "", github: p.github || "",
    experience: p.experience?.length ? p.experience : [{ title: "", org: "", period: "", detail: "" }],
    education: p.education?.length ? p.education : [{ school: "", degree: "", period: "" }],
    projects: p.projects?.length ? p.projects : [{ name: "", link: "", description: "" }],
    skills: (p.skills || []).join(", "), websites: (p.websites || []).join(", "),
  });
  const [busy, setBusy] = useState(false);
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current || user === undefined) return;
    hydrated.current = true;
    const p2 = user?.professional; if (!p2) return;
    setF({
      headline: p2.headline || "", summary: p2.summary || "", location: p2.location || "", photo: p2.photo || "", github: p2.github || "",
      experience: p2.experience?.length ? p2.experience : [{ title: "", org: "", period: "", detail: "" }],
      education: p2.education?.length ? p2.education : [{ school: "", degree: "", period: "" }],
      projects: p2.projects?.length ? p2.projects : [{ name: "", link: "", description: "" }],
      skills: (p2.skills || []).join(", "), websites: (p2.websites || []).join(", "),
    });
  }, [user]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setRow = (key, i, k) => (e) => setF((s) => { const a = [...s[key]]; a[i] = { ...a[i], [k]: e.target.value }; return { ...s, [key]: a }; });
  const addRow = (key, blank) => setF((s) => ({ ...s, [key]: [...s[key], blank] }));
  const delRow = (key, i) => setF((s) => ({ ...s, [key]: s[key].filter((_, j) => j !== i) }));
  const onPhoto = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 1_200_000) { toast.error("Photo under 1MB please."); return; }
    const r = new FileReader(); r.onload = () => setF((s) => ({ ...s, photo: r.result })); r.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        headline: f.headline, summary: f.summary, location: f.location, photo: f.photo, github: f.github,
        experience: f.experience.filter((e) => e.title || e.org),
        education: f.education.filter((e) => e.school || e.degree),
        projects: f.projects.filter((e) => e.name || e.link),
        skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
        websites: f.websites.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.put("/professional", payload);
      if (setUser && user) setUser({ ...user, professional: data.professional });
      if (refreshUser) refreshUser();
      toast.success("Professional profile saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const [parsing, setParsing] = useState(false);
  const resumeRef = useRef(null);
  const parseResume = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/professional/parse-resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const p2 = data.professional || {};
      setF((s) => ({
        ...s,
        headline: p2.headline || s.headline, summary: p2.summary || s.summary, location: p2.location || s.location,
        github: p2.github || s.github,
        experience: p2.experience?.length ? p2.experience : s.experience,
        education: p2.education?.length ? p2.education : s.education,
        projects: p2.projects?.length ? p2.projects : s.projects,
        skills: (p2.skills || []).join(", ") || s.skills,
        websites: (p2.websites || []).join(", ") || s.websites,
      }));
      if (setUser && user) setUser({ ...user, professional: { ...p2, photo: user.professional?.photo || "" }, credits: data.credits_remaining ?? user.credits });
      toast.success(`Profile filled from resume · ${data.charge} credits used`);
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setParsing(false); if (resumeRef.current) resumeRef.current.value = ""; }
  };

  if (!user) return <div className="rud-card rounded-2xl p-6 mt-6 text-center text-sec text-sm">Sign in to build your professional profile.</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-4" data-testid="professional-panel">
      <div className="rud-card rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="resume-upload-card">
        <div className="flex-1">
          <div className="text-sm font-medium flex items-center gap-2"><FileUp size={15} className="text-brand" /> Autofill from your resume</div>
          <div className="text-xs text-sec mt-0.5">Upload a PDF, Word (.docx) or a photo of your resume and My Kind fills in your headline, experience, education, skills & projects. Review, then save.</div>
        </div>
        <input ref={resumeRef} type="file" accept=".pdf,.docx,.txt,image/*" onChange={parseResume} className="hidden" data-testid="resume-input" />
        <button data-testid="resume-upload-btn" onClick={() => resumeRef.current?.click()} disabled={parsing} className="btn-primary rounded-full h-10 px-5 text-sm flex items-center justify-center gap-2 disabled:opacity-60 shrink-0">
          {parsing ? <Loader2 size={15} className="animate-spin" /> : <FileUp size={15} />} {parsing ? "Reading…" : "Upload resume"}
        </button>
      </div>
      <div className="rud-card rounded-2xl p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-4"><Briefcase size={14} /> Professional profile — your public CV</div>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-20 h-20 rounded-full bg-elevated border border-rud grid place-items-center overflow-hidden shrink-0">
            {f.photo ? <img src={f.photo} alt="profile" className="w-full h-full object-cover" data-testid="prof-photo-preview" /> : <User size={26} className="text-sec" />}
          </div>
          <div>
            <input ref={photoRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="prof-photo-file" />
            <button data-testid="prof-photo-btn" onClick={() => photoRef.current?.click()} className="btn-ghost rounded-md h-9 px-3 text-sm flex items-center gap-2"><ImageIcon size={14} /> {f.photo ? "Change photo" : "Add profile photo"}</button>
            {f.photo && <button onClick={() => setF((s) => ({ ...s, photo: "" }))} className="text-xs text-sec hover:text-red-400 ml-2">Remove</button>}
          </div>
        </div>
        <input data-testid="prof-headline" value={f.headline} onChange={set("headline")} placeholder="Headline (e.g. Product Leader · Ex-Acme · Healthtech)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
        <textarea data-testid="prof-summary" value={f.summary} onChange={set("summary")} rows={3} placeholder="About you — a short professional summary" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-3 resize-none" />
        <input data-testid="prof-location" value={f.location} onChange={set("location")} placeholder="Location (city, country)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-3" />

        <div className="mt-5 flex items-center justify-between"><span className="text-sm font-medium flex items-center gap-1.5"><Briefcase size={14} className="text-brand" /> Experience</span><button data-testid="prof-add-exp" onClick={() => addRow("experience", { title: "", org: "", period: "", detail: "" })} className="text-xs text-brand flex items-center gap-1"><Plus size={13} /> Add</button></div>
        {f.experience.map((ex, i) => (
          <div key={i} className="rounded-lg border border-rud p-3 mt-2 space-y-2" data-testid={`prof-exp-${i}`}>
            <div className="flex gap-2"><input value={ex.title} onChange={setRow("experience", i, "title")} placeholder="Title" className="rud-input flex-1 rounded-md px-2.5 py-2 text-sm" /><input value={ex.org} onChange={setRow("experience", i, "org")} placeholder="Company" className="rud-input flex-1 rounded-md px-2.5 py-2 text-sm" /><button onClick={() => delRow("experience", i)} className="text-sec hover:text-red-400"><Trash2 size={14} /></button></div>
            <div className="flex gap-2"><input value={ex.period} onChange={setRow("experience", i, "period")} placeholder="Period (2020–present)" className="rud-input w-40 rounded-md px-2.5 py-2 text-sm" /><input value={ex.detail} onChange={setRow("experience", i, "detail")} placeholder="What you did" className="rud-input flex-1 rounded-md px-2.5 py-2 text-sm" /></div>
          </div>
        ))}

        <div className="mt-5 flex items-center justify-between"><span className="text-sm font-medium flex items-center gap-1.5"><GraduationCap size={14} className="text-brand" /> Education</span><button data-testid="prof-add-edu" onClick={() => addRow("education", { school: "", degree: "", period: "" })} className="text-xs text-brand flex items-center gap-1"><Plus size={13} /> Add</button></div>
        {f.education.map((ed, i) => (
          <div key={i} className="flex gap-2 mt-2" data-testid={`prof-edu-${i}`}><input value={ed.school} onChange={setRow("education", i, "school")} placeholder="School / University" className="rud-input flex-1 rounded-md px-2.5 py-2 text-sm" /><input value={ed.degree} onChange={setRow("education", i, "degree")} placeholder="Degree" className="rud-input flex-1 rounded-md px-2.5 py-2 text-sm" /><input value={ed.period} onChange={setRow("education", i, "period")} placeholder="Years" className="rud-input w-28 rounded-md px-2.5 py-2 text-sm" /><button onClick={() => delRow("education", i)} className="text-sec hover:text-red-400"><Trash2 size={14} /></button></div>
        ))}

        <input data-testid="prof-skills" value={f.skills} onChange={set("skills")} placeholder="Skills (comma separated)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-5" />
        <input data-testid="prof-github" value={f.github} onChange={set("github")} placeholder="GitHub URL (e.g. github.com/you)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-3" />
        <input data-testid="prof-websites" value={f.websites} onChange={set("websites")} placeholder="Other links (comma separated)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-3" />

        <div className="mt-5 flex items-center justify-between"><span className="text-sm font-medium flex items-center gap-1.5"><Sparkles size={14} className="text-brand" /> Projects</span><button data-testid="prof-add-project" onClick={() => addRow("projects", { name: "", link: "", description: "" })} className="text-xs text-brand flex items-center gap-1"><Plus size={13} /> Add</button></div>
        {f.projects.map((pr, i) => (
          <div key={i} className="rounded-lg border border-rud p-3 mt-2 space-y-2" data-testid={`prof-project-${i}`}>
            <div className="flex gap-2"><input value={pr.name} onChange={setRow("projects", i, "name")} placeholder="Project name" className="rud-input flex-1 rounded-md px-2.5 py-2 text-sm" /><input value={pr.link} onChange={setRow("projects", i, "link")} placeholder="Link (GitHub/live)" className="rud-input flex-1 rounded-md px-2.5 py-2 text-sm" /><button onClick={() => delRow("projects", i)} className="text-sec hover:text-red-400"><Trash2 size={14} /></button></div>
            <input value={pr.description} onChange={setRow("projects", i, "description")} placeholder="What it does" className="rud-input w-full rounded-md px-2.5 py-2 text-sm" />
          </div>
        ))}
        <button data-testid="prof-save" onClick={save} disabled={busy} className="btn-primary h-11 px-6 mt-4 flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Briefcase size={16} />} Save professional profile</button>
      </div>

      <a href="/learn" data-testid="prof-learn-cta" className="rud-card rounded-2xl p-5 flex items-center justify-between gap-3 hover:border-brand transition-colors">
        <div className="flex items-center gap-3"><GraduationCap className="text-brand" size={22} /><div><div className="font-medium">Learn & get certified</div><div className="text-sm text-sec">Add credentials to your profile — take or create a course, earn a shareable certificate.</div></div></div>
        <span className="btn-primary h-9 px-4 flex items-center gap-1.5 text-sm shrink-0">Open VishwaGuru</span>
      </a>

      <ProfessionalPosts user={user} photo={f.photo} />
    </motion.div>
  );
}

function ProfessionalPosts({ user, photo }) {
  const [posts, setPosts] = useState([]);
  const [c, setC] = useState({ title: "", body: "", media_url: "", media_type: "image" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/showcase", { params: { category: "professional" } });
      setPosts((data.posts || []).filter((p) => p.is_mine));
    } catch {}
  };
  useEffect(() => { if (user) load(); }, [user]);

  const post = async () => {
    if (!c.title.trim()) { toast.error("Add a title for your post."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/showcase", { ...c, category: "professional" });
      if (data.moderation?.recommendation) toast(data.moderation.recommendation);
      else toast.success("Posted to your professional profile");
      setC({ title: "", body: "", media_url: "", media_type: "image" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rud-card rounded-2xl p-6" data-testid="prof-posts">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-4"><Share2 size={14} /> Post as a professional</div>
      <input data-testid="prof-post-title" value={c.title} onChange={(e) => setC({ ...c, title: e.target.value })} placeholder="What do you want to share? (title)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
      <textarea data-testid="prof-post-body" value={c.body} onChange={(e) => setC({ ...c, body: e.target.value })} rows={3} placeholder="Say more… an update, a win, an idea, a link" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-3 resize-none" />
      <div className="flex gap-2 mt-3">
        <input data-testid="prof-post-media" value={c.media_url} onChange={(e) => setC({ ...c, media_url: e.target.value })} placeholder="Photo or video URL (optional)" className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
        <select value={c.media_type} onChange={(e) => setC({ ...c, media_type: e.target.value })} className="rud-input rounded-lg px-2 text-sm"><option value="image">Photo</option><option value="video">Video</option></select>
      </div>
      <button data-testid="prof-post-submit" onClick={post} disabled={busy} className="btn-primary h-10 px-5 mt-3 flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Post</button>

      {posts.length > 0 && (
        <div className="mt-6 space-y-3" data-testid="prof-post-list">
          <div className="text-xs uppercase tracking-widest text-sec">Your posts</div>
          {posts.map((pp) => (
            <div key={pp.id} className="rounded-lg border border-rud bg-elevated p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-full bg-brand/15 grid place-items-center overflow-hidden shrink-0">
                  {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : <User size={13} className="text-brand" />}
                </div>
                <span className="text-sm font-medium">{pp.author_name}</span>
                {pp.flagged && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-500">reduced reach</span>}
              </div>
              <div className="text-sm font-medium">{pp.title}</div>
              {pp.body && <div className="text-sm text-sec mt-0.5 whitespace-pre-wrap">{pp.body}</div>}
              {pp.media_url && (pp.media_type === "video"
                ? <video src={pp.media_url} controls className="w-full rounded-lg mt-2 max-h-64 object-cover" />
                : <img src={pp.media_url} alt="" className="w-full rounded-lg mt-2 max-h-64 object-cover" />)}
              <div className="text-[11px] text-sec mt-2">♥ {pp.love_count || 0} · 👍 {pp.like_count || 0} · 💬 {pp.comment_count || 0}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function OrgProfile({ user, setUser }) {
  const { refreshUser, lang } = useApp();
  const o = user?.org_profile || {};
  const [f, setF] = useState({
    name: o.name || "", org_type: o.org_type || "Business", industry: o.industry || "",
    tagline: o.tagline || "", description: o.description || "", website: o.website || "",
    location: o.location || "", hq: o.hq || "", founded: o.founded || "", size: o.size || "",
    revenue: o.revenue || "", key_people: o.key_people || "", logo: o.logo || "", banner: o.banner || "", is_private: o.is_private || false,
  });
  const [busy, setBusy] = useState(false);
  const [researching, setResearching] = useState(false);
  const [deckBusy, setDeckBusy] = useState(false);
  const [sigBusy, setSigBusy] = useState(false);
  const [signals, setSignals] = useState(null);
  const [claim, setClaim] = useState({ work_email: "", approver_name: "", approver_title: "", letter: "" });
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [sigHistory, setSigHistory] = useState([]);
  const deckRef = useRef(null);
  const [researchNote, setResearchNote] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get("/org-profile/claim").then(({ data }) => { if (data.claim) setClaimed(true); }).catch(() => {});
    api.get("/org-profile/signals/history").then(({ data }) => setSigHistory(data.history || [])).catch(() => {});
  }, [user]);
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current || user === undefined) return;
    hydrated.current = true;
    const oo = user?.org_profile; if (!oo) return;
    setF({
      name: oo.name || "", org_type: oo.org_type || "Business", industry: oo.industry || "",
      tagline: oo.tagline || "", description: oo.description || "", website: oo.website || "",
      location: oo.location || "", hq: oo.hq || "", founded: oo.founded || "", size: oo.size || "",
      revenue: oo.revenue || "", key_people: oo.key_people || "", logo: oo.logo || "", banner: oo.banner || "", is_private: oo.is_private || false,
    });
  }, [user]);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const onLogo = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 1_200_000) { toast.error("Logo under 1MB please."); return; }
    const r = new FileReader(); r.onload = () => setF((s) => ({ ...s, logo: r.result })); r.readAsDataURL(file);
  };
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name your organization."); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/org-profile", f);
      if (setUser && user) setUser({ ...user, org_profile: data.org_profile });
      if (refreshUser) refreshUser();
      toast.success("Organization profile saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const askRudram = async () => {
    if (!f.name.trim()) { toast.error("Enter the organization name first, then let My Kind fill the rest."); return; }
    setResearching(true); setResearchNote(null);
    try {
      const { data } = await api.post("/org-profile/autofill", { name: f.name, website: f.website, location: f.location, lang });
      const g = data.org_profile;
      setF((s) => ({ ...s, ...g }));
      if (setUser && user) setUser({ ...user, org_profile: g, credits: data.credits_remaining ?? user.credits });
      if (refreshUser) refreshUser();
      setResearchNote({ confidence: data.confidence, note: data.note });
      toast.success("My Kind filled your profile — review, edit and save");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setResearching(false); }
  };
  const onDeck = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setDeckBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/org-profile/parse-deck", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const g = data.org_profile;
      setF((s) => ({ ...s, ...g }));
      if (setUser && user) setUser({ ...user, org_profile: g, credits: data.credits_remaining ?? user.credits });
      toast.success(`Profile filled from your deck · ${data.charge} credits`);
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setDeckBusy(false); if (deckRef.current) deckRef.current.value = ""; }
  };
  const genSignals = async () => {
    if (!f.name.trim()) { toast.error("Enter the company name first."); return; }
    setSigBusy(true);
    try {
      const { data } = await api.post("/org-profile/signals", { name: f.name, industry: f.industry });
      setSignals(data.signals);
      if (setUser && user) setUser({ ...user, credits: data.credits_remaining ?? user.credits });
      api.get("/org-profile/signals/history").then(({ data: h }) => setSigHistory(h.history || [])).catch(() => {});
      toast.success(`Signals generated · ${data.charge} credits`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSigBusy(false); }
  };
  const submitClaim = async () => {
    setClaimBusy(true);
    try {
      const { data } = await api.post("/org-profile/claim", { company: f.name, ...claim });
      setClaimed(true); toast.success(data.message || "Claim submitted");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setClaimBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6" data-testid="org-panel">
      <div className="rud-card rounded-2xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Store size={14} /> Business / Government / Org profile</div>
          <div className="flex items-center gap-2">
            <input ref={deckRef} type="file" accept=".pdf,.docx,.pptx,.txt" onChange={onDeck} className="hidden" data-testid="org-deck-file" />
            <button data-testid="org-deck-upload" onClick={() => deckRef.current?.click()} disabled={deckBusy} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-2 disabled:opacity-60">
              {deckBusy ? <Loader2 size={15} className="animate-spin" /> : <FileUp size={15} />} Upload deck / PDF
            </button>
            <button data-testid="org-autofill" onClick={askRudram} disabled={researching} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-2 disabled:opacity-60">
              {researching ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Let My Kind fill it
            </button>
          </div>
        </div>
        <p className="text-xs text-sec -mt-2 mb-4">For businesses, governments & orgs: build your profile in seconds — upload a pitch deck / PPT / PDF / Word and My Kind autofills it, or let My Kind research public companies for you. Then unlock <b className="text-[var(--rud-text)]">brand & product sentiment, live industry signals</b>, and a path to <b className="text-[var(--rud-text)]">digital transformation</b> — blueprints, strategy, software and marketing from the Business engine. Claim your profile free with a work email + C-level approval. (deck ~1–3 credits · research 6 · signals ~4)</p>

        {researchNote && (
          <div className="flex items-start gap-2 rounded-lg bg-brand/10 border border-brand/30 p-3 text-xs mb-4" data-testid="org-research-note">
            <Sparkles size={14} className="text-brand shrink-0 mt-0.5" />
            <span className="text-[var(--rud-text)]">My Kind filled this with <b>{researchNote.confidence}</b> confidence. {researchNote.note} Please review and edit before saving.</span>
          </div>
        )}

        {f.banner && <img src={f.banner} alt="banner" className="w-full h-28 object-cover rounded-xl border border-rud mb-3" data-testid="org-banner-preview" />}
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <div className="w-16 h-16 rounded-lg bg-elevated border border-rud grid place-items-center overflow-hidden shrink-0">
            {f.logo ? <img src={f.logo} alt="logo" className="w-full h-full object-contain" data-testid="org-logo-preview" /> : <Store size={18} className="text-sec" />}
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="org-logo-file" />
            <button onClick={() => fileRef.current?.click()} className="btn-ghost rounded-md h-9 px-3 text-sm flex items-center gap-2"><Upload size={14} /> Upload logo</button>
            <FileUpload category="general" accept="image/*" label="Upload banner" testId="org-banner"
              onUploaded={(file) => setF((s) => ({ ...s, banner: file.absoluteUrl }))} />
            {f.banner && <button data-testid="org-banner-remove" onClick={() => setF((s) => ({ ...s, banner: "" }))} className="btn-ghost rounded-md h-9 px-3 text-sm flex items-center gap-1"><X size={14} /> Remove banner</button>}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="org-name" value={f.name} onChange={set("name")} placeholder="Organization name" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <select data-testid="org-type" value={f.org_type} onChange={set("org_type")} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm">
            {["Business", "Government", "Non-Profit / NGO", "Institution", "Startup"].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <input data-testid="org-industry" value={f.industry} onChange={set("industry")} placeholder="Industry / sector" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <input data-testid="org-location" value={f.location} onChange={set("location")} placeholder="Location (city, country)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <input data-testid="org-hq" value={f.hq} onChange={set("hq")} placeholder="Headquarters (HQ)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <input data-testid="org-revenue" value={f.is_private ? "" : f.revenue} onChange={set("revenue")} disabled={f.is_private} placeholder={f.is_private ? "Private — revenue hidden" : "Annual revenue (e.g. $12M, 2024)"} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm disabled:opacity-50" />
          <input data-testid="org-founded" value={f.founded} onChange={set("founded")} placeholder="Founded (year)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <input data-testid="org-size" value={f.size} onChange={set("size")} placeholder="Size (e.g. 11-50 employees)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <input data-testid="org-website" value={f.website} onChange={set("website")} placeholder="Website" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm sm:col-span-2" />
        </div>
        <input data-testid="org-key-people" value={f.key_people} onChange={set("key_people")} placeholder="Key people / leadership (founders, CEO…)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-3" />
        <input data-testid="org-tagline" value={f.tagline} onChange={set("tagline")} placeholder="Tagline (one line)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-3" />
        <textarea data-testid="org-description" value={f.description} onChange={set("description")} rows={4} placeholder="About the organization — what you do, who you serve" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-3 resize-none" />
        <label className="flex items-center gap-2 mt-3 text-sm text-sec cursor-pointer" data-testid="org-private-toggle">
          <input type="checkbox" checked={f.is_private} onChange={(e) => setF((s) => ({ ...s, is_private: e.target.checked }))} className="accent-current" />
          Private company — keep revenue blank
        </label>
        <button data-testid="org-save" onClick={save} disabled={busy} className="btn-primary h-11 px-6 mt-4 flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Store size={16} />} Save organization profile</button>
      </div>

      {/* Brand & industry signals */}
      <div className="rud-card rounded-2xl p-6 mt-4" data-testid="org-signals-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><BarChart3 size={14} /> Brand & industry signals</div>
          <button data-testid="org-signals-btn" onClick={genSignals} disabled={sigBusy} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-2 disabled:opacity-60">{sigBusy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate signals</button>
        </div>
        <p className="text-xs text-sec mt-1">Auto-compiled brand & product sentiment and industry signals from live web + public info. Verify before use.</p>
        {sigHistory.length > 0 && (
          <div className="mt-3 border-t border-rud pt-3" data-testid="signals-history">
            <div className="text-[11px] uppercase tracking-widest text-sec mb-2">Sentiment history (track trends)</div>
            <div className="space-y-1">
              {sigHistory.slice(0, 8).map((h) => (
                <div key={h.id} className="flex items-center justify-between text-xs">
                  <span className="text-sec truncate">{h.company} · {new Date(h.created_at).toLocaleDateString()}</span>
                  <span className="text-brand font-medium">{h.brand_score ?? "—"}/100 · {h.brand_label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {signals && (
          <div className="mt-4 space-y-4" data-testid="org-signals-result">
            <div className="rounded-xl border border-rud bg-elevated p-4">
              <div className="flex items-center justify-between"><span className="text-sm font-medium">Sentiment of {f.name || "this brand"}</span>
                <span className="text-sm text-brand font-semibold">{signals.brand_sentiment?.score}/100 · {signals.brand_sentiment?.label}</span></div>
              <p className="text-sm text-sec mt-1">{signals.brand_sentiment?.summary}</p>
            </div>
            {signals.product_sentiment?.length > 0 && (
              <div><div className="text-[11px] uppercase tracking-widest text-sec mb-2">Product sentiment</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {signals.product_sentiment.map((p, i) => (
                    <div key={i} className="rounded-lg border border-rud bg-elevated p-3 text-sm">
                      <div className="flex justify-between"><b className="truncate">{p.product}</b><span className="text-brand">{p.score}</span></div>
                      <div className="text-xs text-sec mt-0.5">{p.note}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {signals.industry_signals?.length > 0 && (
              <div><div className="text-[11px] uppercase tracking-widest text-sec mb-2">Industry signals</div>
                <ul className="space-y-1">{signals.industry_signals.map((s, i) => <li key={i} className="text-sm text-sec flex gap-2"><span className="text-brand">▹</span>{s}</li>)}</ul>
              </div>
            )}
            {signals.note && <p className="text-[10px] text-sec">{signals.note}</p>}
          </div>
        )}
      </div>

      {/* Claim this profile */}
      <div className="rud-card rounded-2xl p-6 mt-4" data-testid="org-claim-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><ShieldCheck size={14} /> Claim this business — free</div>
        <p className="text-xs text-sec mb-4">Own a ready/auto-built profile for free. Verify with your official work email and a letter of approval from a C-level exec (CEO/COO/CMO/CTO/CFO).</p>
        {claimed ? (
          <div className="flex items-center gap-2 text-sm text-green-500" data-testid="org-claim-done"><CheckCircle2 size={16} /> Claim submitted — we'll verify and confirm.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="claim-email" value={claim.work_email} onChange={(e) => setClaim({ ...claim, work_email: e.target.value })} placeholder="Official work email (name@company.com)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
            <input data-testid="claim-approver" value={claim.approver_name} onChange={(e) => setClaim({ ...claim, approver_name: e.target.value })} placeholder="Approver full name" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
            <input data-testid="claim-title" value={claim.approver_title} onChange={(e) => setClaim({ ...claim, approver_title: e.target.value })} placeholder="Approver title (CEO / COO / CMO / CTO / CFO)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
            <input data-testid="claim-company" value={f.name} disabled placeholder="Company" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm opacity-60" />
            <textarea data-testid="claim-letter" value={claim.letter} onChange={(e) => setClaim({ ...claim, letter: e.target.value })} rows={3} placeholder="Paste the C-level approval letter text (or a link)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm resize-none sm:col-span-2" />
            <button data-testid="claim-submit" onClick={submitClaim} disabled={claimBusy} className="btn-primary h-11 px-6 flex items-center gap-2 disabled:opacity-60 sm:col-span-2 justify-center">{claimBusy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Submit claim (free)</button>
          </div>
        )}
      </div>

      {user?.role === "admin" && <AdminClaims />}
    </motion.div>
  );
}

function AdminClaims() {
  const [claims, setClaims] = useState([]);
  const load = async () => { try { const { data } = await api.get("/admin/claims"); setClaims(data.claims || []); } catch (_) {} };
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try { await api.post(`/admin/claims/${id}/${action}`); toast.success(`Claim ${action}d`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rud-card rounded-2xl p-6 mt-4" data-testid="admin-claims">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><ShieldCheck size={14} /> Admin · business claims</div>
      {claims.length === 0 && <p className="text-sec text-sm">No claims yet.</p>}
      {claims.map((c) => (
        <div key={c.id} className="rounded-xl border border-rud bg-elevated p-4 mb-2 flex flex-wrap items-center gap-3 justify-between" data-testid={`claim-${c.id}`}>
          <div className="min-w-0">
            <div className="text-sm font-medium">{c.company} <span className={`text-[10px] uppercase ml-1 ${c.status === "approved" ? "text-green-500" : c.status === "rejected" ? "text-red-400" : "text-sec"}`}>{c.status}</span></div>
            <div className="text-xs text-sec break-all">{c.work_email} · {c.approver_name} ({c.approver_title})</div>
            {c.letter && <div className="text-[11px] text-sec mt-1 line-clamp-2">“{c.letter}”</div>}
          </div>
          {c.status === "pending" && (
            <div className="flex gap-2">
              <button data-testid={`claim-approve-${c.id}`} onClick={() => act(c.id, "approve")} className="btn-primary rounded-full h-9 px-4 text-sm">Approve</button>
              <button data-testid={`claim-reject-${c.id}`} onClick={() => act(c.id, "reject")} className="btn-ghost rounded-full h-9 px-4 text-sm">Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


export { Professional as ProfessionalProfile, MyHabits };
